/**
 * English (en) — source-of-truth translations
 */
const en = {
  reactions: {
    title:   'Reactions',
    all:     'All',
    empty:   'No reactions yet.',
    end:     '— end —',
    viewAll: 'View all reactions',
  },

  common: {
    save:'Save', cancel:'Cancel', delete:'Delete', edit:'Edit', close:'Close',
    back:'Back', loading:'Loading…', more:'More', or:'or', search:'Search',
    yes:'Yes', no:'No', add:'Add', remove:'Remove', create:'Create',
    tryAgain:'Try Again', somethingWrong:'Something went wrong',
    error:'Something went wrong. Please try again.',
    darkMode:'Dark Mode', lightMode:'Light Mode',
    all:'All', today:'Today', yesterday:'Yesterday',
    photo:'Photo', video:'Video', post:'Post',
    open:'Open', leave:'Leave', join:'Join',
    retry:'Retry', next:'Next →', back2:'← Back',
    share:'Share Something', findMembers:'Find Members',
    justNow:'just now', mAgo:(m)=>`${m}m ago`, hAgo:(h)=>`${h}h ago`, dAgo:(d)=>`${d}d ago`,
    editable:'editable',
    expired:'expired',
    done:'Done',
    reposition:'Reposition',
    dragToReposition:'Drag to reposition',
    dragImageHint:'Drag image to choose position',
  },

  auth: {
    legalNoticeSignIn: 'By continuing, you agree to our',
    legalNoticeSignUp: 'By creating an account, you agree to our',
    termsOfService: 'Terms of Service',
    privacyPolicy: 'Privacy Policy',
    legalAnd: 'and',
    tagline:'Prayer · Unity · Disciple-Making',
    welcomeBack:'Welcome back', email:'Email', password:'Password',
    emailPlaceholder:'you@example.com', passwordPlaceholder:'••••••••',
    signIn:'Sign In', forgotPassword:'Forgot password?', createAccount:'Create account →',
    continueWithGoogle:'Continue with Google', continueWithApple:'Continue with Apple',
    joinCommunity:'Join the community', fullName:'Full Name', namePlaceholder:'Your name',
    passwordMinHint:'At least 8 characters', createAccountBtn:'Create Account',
    backToSignIn:'← Back to sign in', checkEmail:'Check your email',
    enterCode:'Enter the 6-digit code we sent you.',
    verificationCode:'Verification Code', codePlaceholder:'123456',
    confirmEmail:'Confirm Email', resendCode:'Resend code', codeResent:'Code resent!',
    confirmSuccess:'Email confirmed! Please sign in.',
    pleaseConfirmFirst:'Please confirm your email first.',
    resetPassword:'Reset password', sendResetCode:'Send Reset Code',
    enterNewPassword:'Enter new password', resetCode:'Reset Code',
    newPassword:'New Password', resetPasswordBtn:'Reset Password',
    resetSuccess:'Password reset! Please sign in.',
    codeSentTo:(email)=>`Reset code sent to ${email}`,
    verificationSentTo:(email)=>`We sent a verification code to ${email}`,
    socialError:'Sign-in failed. Please try again.',
    accountExistsError:'An account with this email already exists. Please sign in with your email and password instead, then link Google/Apple from your profile settings.',
    configError:'Sign-in is temporarily unavailable. Please try again later.',
    signInFailed:'Sign in failed', registrationFailed:'Registration failed',
    confirmFailed:'Confirmation failed', resetFailed:'Reset failed',
    sendResetFailed:'Failed to send reset code',
    setNewPasswordTitle:'Set your password',
    setNewPasswordDesc:'Your account was created by an admin. Please set a permanent password to continue.',
    setPasswordBtn:'Set Password & Sign In',
    confirmPassword:'Confirm Password',
    passwordMismatch:'Passwords do not match',
    verse:'"Commit to the LORD whatever you do, and he will establish your plans." — Proverbs 16:3',
  },

  username: {
    title:'One last step!',
    desc:'Choose a username for your Ignite profile. This is how others will find and mention you.',
    welcome:(name)=>`Welcome, ${name} 👋`,
    placeholder:'yourname', available:'Username available ✓',
    taken:'Already taken', invalid:'3–30 characters, letters / numbers / . / _ only',
    continue:'Continue to Ignite →',
    hint:'You can change this later in your profile settings.',
    errorSave:'Could not save username', preferSpanish:'¿Prefieres español?',
    next:'Next →',
    dobTitle:'One more thing',
    dobDesc:'We need your date of birth to make sure Ignite is the right fit for you.',
    dobLabel:'Date of Birth',
    dobRequired:'Please enter your date of birth.',
    finishSetup:'Finish Setup →',
    blockedTitle:'Age Requirement Not Met',
    blockedMsg:'Ignite is for users 13 and older. Ask a parent or guardian to help you get started.',
    enterDifferentDob:'← Enter a different date',
    stepOf:(step,total)=>`Step ${step} of ${total}`,
  },

  nav: {
    home:'Home', homeDesc:'Feed, stories & your journal',
    people:'People', peopleDesc:'Who you pray for & disciple',
    feed:'Feed', feedDesc:'Community activity',
    stories:'Stories', storiesDesc:'Stories from around the world',
    members:'Members', membersDesc:'Find & connect',
    connections:'Connections', connectionsDesc:'Your network',
    communities:'Communities', communitiesDesc:'Your circles',
    prayer:'Prayer', prayerDesc:'Intercede together', calShort:'Cal', calLabel:'Calendar',
    bibleStudy:'Bible Study', bibleStudyDesc:'Dive into Scripture',
    myLineage:'My Lineage', myLineageDesc:'Your spiritual family tree',
    notifications:'Notifications', notifDesc:'Mentions & updates',
    courses:'Grow Deeper', coursesDesc:'Deepen your faith', growShort:'Grow',
    profile:'Profile', profileDesc:'Your account',
    prayNow:'Pray Now', prayNowDesc:'Speak a prayer aloud',
    myStory:'My Story', myStoryDesc:'Shape & share your testimony',
    journal:'My Journal', journalShort:'Journal', journalDesc:'Your private spiritual record',
    faq:'Help & FAQ', faqDesc:'Frequently asked questions',
    notifSettings:'Notification Settings', notifSettingsDesc:'Manage how Ignite contacts you',
    settings:'Settings', settingsDesc:'Account & preferences',
    messages:'Messages', messagesDesc:'Chats & direct messages',
    admin:'Admin', adminDesc:'Manage community', more:'More',
    signOut:'Sign out',
    mainNav:'Main navigation',
    quickActions:{
      shareReflection:'Share a reflection', addPrayer:'Add prayer request',
      findCommunity:'Find a community', startCourse:'Grow Deeper',
    },
    // One-time notice shown after the navigation restructure. Plain list of
    // where things went — no tour, no coach marks.
    whatMoved:{
      title:'A few things moved',
      intro:'We tidied up the menu so everything has a home you can see. Here is where things are now:',
      home:'Feed, Stories and My Journal are together under <b>Home</b>.',
      people:'Recipients and My Lineage are together under <b>People</b>.',
      grow:'Bible Study is under <b>Grow</b>.',
      prayer:'Pray Now is on the <b>Prayer</b> calendar.',
      account:'Profile and Settings are still under your photo, top right.',
      gotIt:'Got it',
    },
  },

  people: {
    title:'People',
    tabs:{ people:'People', encounters:'Encounters', lineage:'Lineage' },
    // One per tab — PeopleLayout looks these up by the active tab, so a tab
    // without an entry renders the raw key in the header. Keyed the same as
    // `tabs` above; keep the two in step.
    subtitles:{
      people:'Pray for them. Walk with them.',
      encounters:'Conversations worth preparing for',
      lineage:'Your spiritual family tree',
    },
    empty:'No one here yet',
    emptyDesc:'Add someone you want to pray for and they will show up here.',
    addPerson:'Add a person',
    searchPlaceholder:'Search by name',
    // Reached by the filters as well as the search box, so it cannot say "search".
    noMatches:'No one here matches.',
    filters:{
      all:'Everyone',
      // Reads as a sentence with the select next to it: "Praying: Everyone".
      // The colon lives here rather than in CSS so a translation can drop it.
      prayingLabel:'Praying:',
      oikos:'Not yet following Jesus',
      oikosHint:'Hides anyone already following Him',
    },
    prayedTimes:(n) => `${n} ${n === 1 ? 'prayer' : 'prayers'}`,
    detail:{
      sections:'About this person',
      about:'About', making:'Walking With', history:'History',
      notFound:'We could not find that person.',
      editDetails:'Edit their details',
    },
    about:{
      praying:'What you are praying for',
      // "Your prayers", not "their score". The counts here measure the user's
      // own faithfulness, which is a fair thing to count. Nothing on this screen
      // measures the person.
      yourPrayers:'Your prayers for them',
      prayersLabel:'prayers',
      streakLabel:'day streak',
      lastPrayedLabel:'last prayed',
      prayedToday:'Prayed today',
      details:'Details',
      journey:'Where they are',
      added:'Added',
      inviteSent:'Invitation sent',
      inviteJoined:'Joined Ignite',
    },
    making:{
      whereTheyAre:'Where they are',
      whereHint:'Your own read on where this person is. Nothing changes it but you.',
      whatYouKnow:'What you know about them',
      knowHint:'Notes for you, and what the guide is built from. Only you can see any of this.',
      story:'Their story', storyPh:'Where they have come from…',
      weighing:'What is weighing on them', weighingPh:'What is hard for them right now…',
      faithBg:'Faith background', faithBgPh:'Church, family, anything they have said about God…',
      notes:'Anything else', notesPh:'Things worth remembering…',
      profileSaved:'Saved',
      stageSaved:'Saved',
      // Said in words rather than left to a highlighted button. This is the most
      // consequential thing a user can record about another person.
      crossedIntoFaith:(name)=>`You have recorded that ${name} is now following Jesus. That is worth stopping over.`,
      harvestLineage:(name)=>`${name} is helping others follow Jesus. Would you like to add them to your lineage?`,
      lineageAsked:'Asked. They will see your invitation.',
    },
    // Conversations and the stage log moved here from `making` along with the
    // sections that render them — both are a record of what happened, which is
    // what this tab is for.
    history:{
      prayer:'Prayer',
      lastPrayed:(date)=>`Last prayed ${date}`,
      conversations:'Conversations',
      noneYet:(name)=>`No conversations with ${name} yet.`,
      // A conversation was debriefed but the "what did you learn" answer skipped.
      // Every debrief question is skippable, so this is a normal outcome.
      noNotes:'No notes from this one.',
      stageHistory:'What has changed',
      movedFromTo:(from,to)=>`${from} → ${to}`,
      crossedTag:'came to faith',
    },
    stagePicker:{
      boundary:'They come to trust Jesus here',
      suggested:'Suggested',
    },
  },

  // ── Encounters ───────────────────────────────────────────────────────────
  // Planning, holding and reflecting on one real conversation.
  //
  // NO CRM LANGUAGE. Nothing here counts, ranks, scores, chases or measures a
  // person. No "leads", "pipeline", "follow-up due", "engagement", no streaks
  // tied to someone else, and no progress framing anywhere. If a string would
  // fit in a sales tool, it does not belong here.
  encounters:{
    aConversation:'A conversation',
    planOne:'Plan a conversation',
    planWith:(name)=>`A conversation with ${name}`,
    theyAre:(stage)=>`Right now: ${stage}`,
    firstHint:'This looks like your first one with them. Start by finding out where they are.',
    notAvailable:'This is not available.',

    settings:{
      coffee:'Coffee', truck:'In the truck', meal:'Over a meal',
      walk:'On a walk', jobsite:'At work', boat:'On the boat', other:'Somewhere else',
    },
    whereLabel:'Where will you be?',
    customPlaceholder:'Where will you be?',

    modeLabel:'What kind of conversation?',
    modeDiscovery:'Find out where they are',
    modeDiscoveryDesc:'You are not sure where they stand. Listen, and let them tell you.',
    modeStandard:'Go one step further',
    modeStandardDesc:'You have a sense of where they are, and want to go a little deeper.',
    firstTimeNote:'You have not recorded a conversation with them yet — finding out where they are is usually the better first step.',

    goalLabel:'Anything you are hoping for?',
    goalHint:'Optional. Leave it blank and nothing will be assumed.',
    goalPlaceholder:'I want to ask how he is really doing…',

    knowsLabel:'What this will be built from',
    knowsStage:(stage)=>`Where you said they are: ${stage}`,
    knowsPrior:(n)=>n===1?'One earlier conversation':`${n} earlier conversations`,
    knowsNothing:'You have not written anything about them yet. The guide will be more general.',
    knowsEdit:'Add what you know',

    generate:'Build the guide',
    genShaping:'Shaping the conversation…',
    genWriting:'Writing it out…',
    genPatience:'This takes a moment. It is checking every line against where they are.',
    genEmpty:'Nothing usable came back. Please try again.',

    readyToGo:'ready',
    needsGenerating:'no guide yet',
    plannedFor:(date)=>`for ${date}`,
    noGuideYet:'There is no guide for this conversation yet.',

    // Opening a conversation that already has a guide. The point of this copy is
    // that the guide was not lost — it says so plainly, because the bug it
    // replaces made people believe their planning had been thrown away.
    alreadyPlannedTitle:'This one is already planned',
    alreadyPlannedBody:'Your guide is saved. Open it whenever you are ready — it will be the same one you built.',
    openGuide:'Open the guide',
    startOver:'Start over',
    startOverConfirm:'Build a new guide for this conversation? This replaces the guide you already have.',
    startOverNote:'Building this will replace the guide you already have.',
    regenerate:'Build a new guide',
    guideMenu:'More',
    rowMenu:(name)=>`More for ${name}`,
    deletePlan:'Delete this plan',
    deleteConfirm:'Delete this planned conversation? The guide goes with it.',

    ifThey:(cond)=>`If ${cond}`,
    next:'Next',
    wereDone:'We are done',
    captureNote:'Write something down',
    notePlaceholder:'What he just said…',

    debriefTitle:(name)=>`After talking with ${name}`,
    debriefSub:'Three questions. Answer what you want and skip the rest.',
    qLearned:'What did you learn about them?',
    qLearnedPh:'Anything you did not know before…',
    qGodMoved:'Where did you see God?',
    qGodMovedPh:'Even something small…',
    qNextStep:'What is the one next step?',
    qNextStepPh:'One thing, not five…',
    debriefNeedsOne:'Answer at least one of the three.',
    debriefSave:'Save this',
    debriefSaving:'Saving…',
    debriefSaved:'Saved.',
    noSuggestion:'Nothing further to suggest — your own notes are what matter here.',

    proposalsTitle:'Some things you might keep',
    proposalsSub:'Suggestions only. Nothing is saved until you choose it.',
    propProfile:'Add to what you know about them',
    propNextStep:'Keep this as the next step',
    propPrayer:'Add these to your prayers',
    propPrayerNote:'These go onto your prayer calendar for today. Your own prayer notes are left alone.',
    applySelected:'Keep what I ticked',
    applied:'Saved',

    stageProposalTitle:'Where they are',
    stageProposal:(name,stage)=>`This conversation might suggest ${name} is now at ${stage}.`,
    stageYoursToMake:'This one is yours to decide. Tap a stage only if you believe it, and it will be recorded with today’s date.',
    allDone:'Done',

    emptyTitle:'No conversations yet',
    emptyDesc:'Pick someone and plan a real conversation — where you will be, and what you are hoping for.',
    needAPersonFirst:'Add someone to your people first.',
    pickUp:'Pick up where you left off',
    recent:'Earlier conversations',
    whoWith:'Who is this with?',
  },

  feed: {
    postTypes:{
      reflection:'Reflection', scripture:'Scripture',
      prayer:'Prayer', praise:'Praise', surrender:'Surrender',
      prayer_request:'Prayer Request',
      // Banner taglines
      scriptureTagline:'His Living Word',
      reflectionTagline:'From the Heart',
      prayerTagline:'Lifting It Up',
      praiseTagline:'Glory to God',
      surrenderTagline:'Laying It Down',
      prayerRequestTagline:'Standing With You',
    },
    visibility:{
      public:'Public', connections:'Connections & Groups',
      friends:'Connections Only', groups:'My Groups Only',
    },
    filters:{ all:'All', reflections:'Reflections', scripture:'Scripture', prayer:'Prayer', praise:'Praise', surrender:'Surrender' },
    placeholders:{
      scripture:'Share what this passage means to you…',
      prayer:'What would you like prayer for?',
      reflection:"What's on your heart today?",
      praise:'Share how God has moved… (optional)',
      surrender:'Share what this surrender means to you… (optional)',
    },
    composePlaceholder:"What's on your heart today?",
    modalTitle:'Share with Community',
    editPost:'Edit Post',
    postType:'Post type', visibleTo:'Visible to',
    mentionHint:'Use @name to mention · #tag for hashtags',
    writeSomething:'Please write something first',
    failedPost:'Failed to post', failedSave:'Failed to save', failedLoad:'Could not load posts',
    deleteConfirm:'Delete this post?',
    noPosts:'Nothing here yet', beFirst:'Be the first to share something with the community.',
    noPostsHashtag:(tag)=>`No posts tagged #${tag}`,
    somethingWrong:'Something went wrong',
    reply:'Reply', cancelReply:'Cancel',
    writeComment:'Write a comment…', writeReply:'Write a reply…',
    shareSomething:'Share Something',
    feedTitle:'Feed', feedSubtitle:'See what God is doing in your community',
    feedTab:'Feed', savedTab:'Saved',
    noSavedPosts:'No saved posts yet',
    noSavedPostsDesc:'Tap the bookmark icon on any post to save it here.',
    repost:'Repost', repostTitle:'Repost',
    addComment:'Add a comment… (optional)',
    cancel:'Cancel',
    reportPost:'Report post', reportCommunity:'Report community', reportPrayer:'Report prayer request',
    // Structured post inputs
    praiseWhatLabel:'What are you praising God for?',
    praiseWhatPlaceholder:'e.g. God healed my father, the promotion came through…',
    praiseMissing:'Please share what you\'re praising God for.',
    surrenderWhatLabel:'What are you surrendering to God?',
    surrenderWhatPlaceholder:'e.g. My fear of the future, control over my finances…',
    surrenderMissing:'Please share what you\'re surrendering to God.',
    verseLabel:'Attach a verse (optional)',
    versePlaceholder:'e.g. John 3:16 or Psalm 23',
    verseLookup:'Look up',
    verseNotFound:'Verse not found — try a different format (e.g. "John 3:16").',
    addPrayerText:'Add a written prayer (optional)',
    prayerTextLabel:'Your prayer',
    prayerTextPlaceholder:'Lord, I lift up…',
    // Share to Feed (from PrayNow / BibleStudy / PrayerWall)
    shareToFeed:'Share to Feed',
    shareToFeedTitle:'Share to Feed',
    prayNowShareTitle:'Share This Prayer',
    prayNowShareNote:'Your prayer session will appear on the feed so others can pray alongside you.',
    bibleStudyShareTitle:'Share Bible Study',
    bibleStudyShareNote:'Share your study and reflections with your community.',
    prayerWallShareTitle:'Share Prayer Request',
    prayerWallShareNote:'This request has limited visibility. You can share it to the feed at the same or a more restricted level — but not more openly than the original.',
    prayerWallPrivateNote:'This prayer request is private and cannot be shared to the feed.',
    sharedSuccess:'Shared to your feed!',
    loadMore:'Load older posts',
    loadingMore:'Loading…',
  },

  invite: {
    title: "You're invited to Ignite",
    subtitle: 'Ignite is a faith community for prayer, Scripture, and growing as a follower of Jesus — together. Someone wanted you here.',
    join: 'Join Ignite',
    haveAccount: 'I already have an account',
  },
  testimony: {
    // ── List ──
    listTitle: 'My Story',
    listIntro: 'Your stories of life with God. Write as many as you like — each one a chapter of how he has been at work.',
    newStory: 'New story',
    empty: "You haven't written a story yet",
    emptyHint: 'Your story is one of the most powerful things you can share. Start with a few notes — Ignite will help you shape them.',
    emptyCta: 'Write your first story',
    loadError: "We couldn't load your stories.",
    saveError: 'Something went wrong. Please try again.',
    deleteConfirm: (title) => `Delete "${title || 'this story'}"? This can't be undone.`,
    untitled: 'Untitled story',
    draft: 'Draft',
    draftEmpty: 'Not written yet — tap to continue.',
    editStory: 'Edit',
    deleteStory: 'Delete',
    manageStories: 'Manage',
    profileSection: 'Their stories',
    profileSectionOwn: 'My Stories',

    // ── Wizard / editor ──
    stepLabels: ['Start', 'Notes', 'Story', 'Share'],
    pickTemplateTitle: 'Where does your story begin?',
    pickTemplateIntro: "Pick the kind of story this is. Not everyone has a dramatic conversion — and that's okay. You can change this any time.",
    titleLabel: 'Give it a title',
    titlePlaceholder: 'e.g. The year everything fell apart',
    editTitle: 'Edit story',
    editNotes: 'Edit answers',
    next: 'Next',
    back: 'Back',
    skip: 'Skip',
    generate: 'Write my story',
    regenerate: 'Rewrite',
    generating: 'Writing…',
    questionOf: (n, total) => `Question ${n} of ${total}`,
    promptStepHint: "A sentence or two is plenty. Skip anything that doesn't fit your story.",
    draftTitle: 'Your story',
    draftHint: "Here's a first draft from your notes. Make it yours — edit anything, or rewrite it.",
    keepStory: 'Keep my story',
    coverLabel: 'Cover image (optional)',
    coverUpload: 'Upload image',
    coverGenerate: 'Generate with AI',
    coverGenerating: 'Creating image…',
    coverRemove: 'Remove',
    coverNeedsDraft: 'Write your story first, then AI can picture it.',
    coverFailed: "Couldn't add the image. Please try again.",
    needNotes: 'Answer a few prompts first, then I can help.',
    yourNotes: 'Your notes',
    yourStory: 'Your story',
    editorPlaceholder: 'Your story will appear here. You can edit it freely.',
    visibilityTitle: 'Who can see this?',
    visibilityOptions: {
      private:     { label: 'Only me',              desc: 'A private draft. No one else can see it.' },
      connections: { label: 'My connections',       desc: 'People you are connected with can read it on your profile.' },
      public:      { label: 'Anyone with the link', desc: 'Shareable with anyone — and, after a quick review, featured in the global Movement feed.' },
    },
    getLink: 'Create share link',
    shareAgain: 'Share again',
    finish: 'Done',
    save: 'Save',
    share: 'Share',
    saved: 'Saved',
    shared: 'Shared',
    linkCopied: 'Link copied',
    fallbackNote: 'Drafted from your notes',
    shareTitle: 'My story',
    shareText: 'I wanted to share my story with you.',

    // ── Sharing with the Movement feed (shown when "public" is chosen) ──
    movementSection: 'Share with the Movement',
    movementIntro: 'Public stories are added to the global Movement feed so believers around the world can be encouraged. Our team reviews each one before it appears.',
    locationLabel: 'Where are you (region / country)?',
    locationPlaceholder: 'e.g. Nairobi, Kenya',
    categoryLabel: 'Theme',
    categoryNone: 'Choose a theme (optional)',
    anonymousLabel: 'Show on the Movement feed anonymously',
    anonymousNamePlaceholder: 'Display name (e.g. "A believer in Iran")',
    publicConsent: 'Approved stories appear publicly on Ignite and may be shown in search engines like Google. If you mark it anonymous, your name is hidden on the Movement feed.',
    movementPending: 'Pending review — your share link works now; it joins the Movement feed once our team approves it.',
    movementLive: 'Live in the Movement.',
    movementViewInFeed: 'View in the Movement',
    movementCategories: {
      salvation: 'Salvation',
      healing: 'Healing',
      provision: 'Provision',
      persecution: 'Persecution',
      revival: 'Revival',
      other: 'Story',
    },

    // ── Public view ──
    publicTitle: (name) => `${name}'s story`,
    joinCta: 'Join Ignite',
    learnMore: 'Learn more about Ignite',
    notFoundTitle: 'Story not found',
    notFoundBody: 'This story may have been removed or made private.',

    // ── Shared closing reflection (appended to every template) ──
    sharedPrompts: {
      grace: {
        label: "Looking back, where do you most see God's love, grace, or mercy?",
        placeholder: 'What he did, how he met you, what you want others to know about him…',
        example: 'e.g. I did not deserve it, but he never let go of me — and I want people to know he does the same for them.',
      },
    },

    // ── Templates (starting points) ──
    templates: {
      classic: {
        label: 'How I met Jesus',
        desc: 'The classic arc — life before, the turning point, and life now.',
        prompts: {
          before:  { label: 'What was your life like before you knew Jesus?', placeholder: 'What you lived for, what was missing, what was hard…', example: 'e.g. I chased success and approval, but underneath I felt empty and anxious.' },
          meeting: { label: 'How did you come to follow Jesus?', placeholder: 'What happened? Who or what did God use?', example: 'e.g. A friend invited me to church during a low season, and something finally made sense.' },
          after:   { label: 'How is your life different now, walking with him?', placeholder: "What's changed — even if it's not perfect?", example: 'e.g. I still struggle, but I have a peace and a purpose I never had before.' },
        },
      },
      deeper: {
        label: 'Grew up in faith',
        desc: "You've always believed — this is the story of going deeper.",
        prompts: {
          upbringing:   { label: 'What was faith like growing up?', placeholder: 'Church, family, what you believed and why…', example: 'e.g. I went to church every Sunday and never doubted God existed.' },
          turningPoint: { label: 'When did faith become truly your own?', placeholder: 'A moment or season it stopped being inherited…', example: 'e.g. Away from home for the first time, I had to decide if I actually believed it for myself.' },
          nowFaith:     { label: 'How are you growing or going deeper now?', placeholder: 'Where God is at work in you today…', example: 'e.g. I am learning to trust him with things I used to try to control.' },
        },
      },
      prodigal: {
        label: 'Coming back',
        desc: 'You knew faith, drifted for a season, and were drawn back.',
        prompts: {
          drifting:  { label: 'What pulled you away, and what was that season like?', placeholder: 'Honestly — what happened and how it felt…', example: 'e.g. After a loss I stopped praying. I told myself I was fine without it.' },
          awakening: { label: 'What started drawing you back?', placeholder: 'The nudge, the ache, the person, the moment…', example: 'e.g. A restlessness I could not shake, and an old friend who never gave up on me.' },
          return:    { label: 'What is your walk with God like now?', placeholder: 'Where you are today, even if still healing…', example: 'e.g. I came back expecting shame and found I was welcomed home.' },
        },
      },
      waiting: {
        label: 'A season of waiting',
        desc: "When life didn't go to plan and God felt slow or silent.",
        prompts: {
          longing:   { label: 'What were you hoping or waiting for?', placeholder: 'The thing you longed for…', example: 'e.g. We prayed for a child for years with no answer.' },
          wrestling: { label: 'What was hard about the waiting?', placeholder: 'With God, with yourself, with others…', example: 'e.g. I was angry at God and tired of pretending I was okay.' },
          meeting:   { label: 'Where did you meet God in it?', placeholder: "Even if it's still unresolved…", example: 'e.g. I did not get my answer, but I found he was nearer than I thought.' },
        },
      },
      miracle: {
        label: 'An answered prayer',
        desc: 'A specific time God moved in an unmistakable way.',
        prompts: {
          need:   { label: 'What was the situation or need?', placeholder: 'Set the scene…', example: 'e.g. The doctors said there was nothing more they could do.' },
          prayer: { label: 'How did you pray, and who prayed with you?', placeholder: 'The crying out, the people beside you…', example: 'e.g. Our whole small group prayed over us every night for a month.' },
          answer: { label: 'What happened, and how did it change your trust in God?', placeholder: 'The turn, and what it did in you…', example: 'e.g. The next scan was clear. I will never doubt that he hears.' },
        },
      },
      freeform: {
        label: 'Start from scratch',
        desc: 'Not sure where it fits? Tell it your own way.',
        prompts: {
          part1: { label: 'Where does your story begin?', placeholder: 'Start wherever feels right…', example: 'e.g. Who you were and where you were.' },
          part2: { label: 'What happened — the heart of it?', placeholder: 'The turning point or the part that matters most…', example: 'e.g. The moment, the struggle, or the change.' },
          part3: { label: 'Where are you now, and what do you want others to take from it?', placeholder: 'Where it has led you…', example: 'e.g. What you would want a friend to know.' },
        },
      },
    },
  },

  // ── Movement Stories — "Around the World" feed ─────────────────────────────
  movement: {
    feedTab: 'Movement',
    screenTitle: 'Movement Stories',
    screenSubtitle: 'See what God is doing across the globe — and share your own.',
    tabWorld: 'Around the World',
    tabMap: 'Map',
    tabExplore: 'Explore',
    tabMine: 'My Stories',
    map: {
      statStories:  (n) => `${n} ${n === 1 ? 'story' : 'stories'}`,
      statCountries:(n) => `${n} ${n === 1 ? 'country' : 'countries'}`,
      statStates:   (n) => `${n} US ${n === 1 ? 'state' : 'states'}`,
      hint: 'Drag to spin · scroll to zoom · tap a country',
      hintStates: 'Tap a state · zoom out for the world',
      resetGlobe: 'Reset view',
      backToWorld: 'Back to world',
      clearSelection: (place) => `Clear ${place}`,
      storiesFrom: (place) => `Stories from ${place}`,
      noneHereYet: (place) => `No stories from ${place} yet — be the first.`,
      oneInView: '1 story in view',
      nInView: (n) => `${n} stories in view`,
      noneInView: 'No stories in this part of the world yet. Try zooming out.',
      showMore: 'Show more',
      showOnMap: 'Show on the map',
      oneStoryHere: '1 story',
      nStoriesHere: (n) => `${n} stories`,
      approximate: 'approximate location',
      unknownPlace: 'Somewhere in the world',
      thisPlace: 'This place',
      close: 'Close',
      emptyTitle: 'No stories on the map yet',
      emptyBody: 'As stories come in with a location, they will appear here.',
    },
    dismiss: 'Dismiss',
    introTitle: 'Around the World',
    introBody: 'See what God is doing across the globe — and share your own story.',
    submit: 'Share a story',
    readStory: 'Read story',
    whoReacted: 'See reactions',
    sourcePrefix: 'Source:',
    report: 'Report',
    anonymous: 'Anonymous',
    back: 'Back',

    emptyTitle: 'No stories yet',
    emptyBody: 'Be the first to share what God is doing where you are.',
    somethingWrong: 'Something went wrong',
    failedLoad: 'Could not load stories.',
    retry: 'Retry',
    loadMore: 'Load more stories',
    loadingMore: 'Loading…',
    notFoundTitle: 'Story not found',
    notFoundBody: 'This story may have been removed or is no longer available.',

    // Discovery layout
    sectionFeatured: 'Featured',
    mostLoved: 'Most loved',
    latest: 'Latest stories',
    seeAll: 'See all',
    allStories: 'All stories',
    regionLabel: 'Region',
    regionAuto: 'Auto-detect from location',
    byRegion: {
      africa: 'From Africa',
      asia: 'From Asia',
      middle_east: 'From the Middle East',
      europe: 'From Europe',
      americas: 'From the Americas',
      oceania: 'From Oceania',
    },

    // Submission modal
    submitTitle: 'Share your story',
    submitIntro: 'Tell believers around the world what God has done. Our team reviews each story before it appears.',
    fromExisting: 'Start from one of your stories',
    startBlank: 'Start from scratch',
    untitledStory: 'Untitled story',
    titleLabel: 'Title',
    titlePlaceholder: 'e.g. He met me in the waiting',
    locationLabel: 'Where are you (region / country)?',
    locationPlaceholder: 'e.g. Nairobi, Kenya',
    categoryLabel: 'Theme',
    categoryNone: 'Choose a theme (optional)',
    storyLabel: 'Your story',
    storyPlaceholder: 'Share what happened and how you saw God at work…',
    anonymousLabel: 'Share anonymously',
    anonymousNamePlaceholder: 'Display name (e.g. "A believer in Iran")',
    bodyRequired: 'Please write your story first.',
    submitFailed: 'Could not submit. Please try again.',
    submitForReview: 'Submit for review',
    cancel: 'Cancel',
    close: 'Close',
    submittedTitle: 'Submitted for review',
    submittedBody: "Thank you for sharing. We'll review your story and publish it soon.",
    publicConsent: 'Approved stories appear publicly on Ignite and may be shown in search engines like Google. Personal identities you mark as anonymous are kept private.',

    // Sharing
    shareTitle: 'Share',
    shareText: 'See what God is doing around the world.',
    shareCopyOrShare: 'Copy / Share',
    sendInMessage: 'Send in a message',
    linkCopied: 'Link copied',
    shared: 'Shared',
    shareSentTo: (name) => `Sent to ${name}`,
    shareSendSub: 'Choose who to send this story to.',
    shareSearchConnections: 'Search connections',
    shareNoConnections: 'No connections yet.',
    shareDmNotePlaceholder: 'Add a note (optional)',
    openInApp: 'Open in the app',
    joinCta: 'Join Ignite',
    publicTagline: 'Learn about Ignite',

    categories: {
      salvation: 'Salvation',
      healing: 'Healing',
      provision: 'Provision',
      persecution: 'Persecution',
      revival: 'Revival',
      other: 'Story',
    },

    // Admin console
    admin: {
      title: 'Movement Stories',
      tabPending: 'Pending',
      tabPublished: 'Published',
      tabDrafts: 'Drafts',
      create: 'Create story',
      aiGenerate: 'AI generate',
      generating: 'Generating…',
      regionLabel: 'Region (optional)',
      regionPlaceholder: 'e.g. Southeast Asia',
      themeLabel: 'Theme (optional)',
      themePlaceholder: 'e.g. house-church revival',
      approve: 'Approve',
      reject: 'Reject',
      retire: 'Retire',
      edit: 'Edit',
      delete: 'Delete',
      save: 'Save',
      feature: 'Feature',
      featured: 'Featured',
      rejectReason: 'Reason (optional, shared with submitter)',
      publishNow: 'Publish immediately',
      confirmDelete: 'Delete this story permanently?',
      confirmRetire: 'Retire this story? It will be hidden from the feed.',
      emptyPending: 'No submissions waiting for review.',
      emptyPublished: 'No published stories yet.',
      bySubmitter: 'by',
      source: 'Source',
      sourceSystem: 'Curated',
      sourceUser: 'Submission',
      sourceUrlLabel: 'Source link (real, published story)',
      sourceNameLabel: 'Source name (optional)',
      sourceNamePlaceholder: 'e.g. Open Doors, Christianity Today',
      viewSource: 'View source',
      savedToast: 'Saved',
      coverageTitle: 'Coverage',
      coverageCountries: 'Countries',
      coverageStates: 'US states',
      coverageShowMissing: 'Show missing',
      coverageHide: 'Hide',
      geoTitle: 'Map placement',
      geoCity: 'City (optional)',
      geoCountry: 'CC',
      geoState: 'ST',
      geoLat: 'Latitude',
      geoLng: 'Longitude',
      geoHint: 'Leave the coordinates blank to pin at the centre of the state or country.',
      geoCurrent: (p) => `Currently pinned at ${p}. Entering coordinates fixes the pin by hand — it will not be re-derived.`,
      geoPrecision: {
        city: 'an exact point',
        state: 'the centre of the state',
        country: 'the centre of the country',
      },
    },
  },

  prayer: {
    title:'Prayer', subtitle:'The people on your heart',
    tabTitles: {
      calendar:   'Prayer Calendar',
      discover:   'Discover Requests',
      recipients: 'Prayer Recipients',
      requests:   'Prayer Requests',
    },
    tabSubtitles: {
      calendar:   'Faithful in prayer, day by day',
      discover:   'Lift someone up today',
      recipients: 'The people on your heart',
      requests:   'Bring your needs before the Body',
    },
    addMenu: {
      label:     '+ Add',
      person:    'Prayer recipient',
      personSub: 'Private — only visible to you',
      request:   'Prayer request',
      requestSub:'You control who sees it',
    },
    witness: {
      title: 'Where are they on the journey?',
      intro: 'Six phases, mapped to the Four Chairs — from breaking up hard ground to releasing a multiplying disciple.',
      preFaithLabel: 'Before faith — Chair 1: Come & See',
      postFaithLabel: 'Following Jesus — Chairs 2, 3 & 4',
      till: 'Till',
      soil: 'Soil',
      seed: 'Seed',
      sprout: 'Sprout',
      plant: 'Plant',
      harvest: 'Harvest',
      tillHint: 'Break up hard ground — pray, serve, listen with no strings.',
      soilHint: 'Heart is softening — share life, ask questions, invite them in.',
      seedHint: 'Plant the Word — open Scripture together, invite to a DBS.',
      sproutHint: 'Fresh faith — care, protect, baptism, foundational habits.',
      plantHint: 'Deep roots — equip for ministry, hand off, train to reach their own oikos.',
      harvestHint: 'Multiplying — they\'re discipling others; shift into coaching.',
      oikosFilter: 'Oikos',
      oikosFilterHint: 'Only people who don\'t yet follow Jesus',
      startDiscipling: 'Start discipling',
      inviteToIgnite: 'Invite to Ignite',
      shareTitle: 'Join me on Ignite',
      shareModalTitle: 'Send your invite',
      shareModalIntro: (name) => `Personalize the message for ${name}. Their invite link is added automatically.`,
      shareModalUrlLabel: 'Your invite link',
      shareNow: 'Open share sheet',
      linkCopied: 'Invite link copied',
      inviteSent: 'Invite shared',
      discipleSent: 'Discipleship request sent',
      discipleNeedsConnection: 'Connect with them first to start discipling',
      // Stage-aware templates for the personalized invite message. {name}
      // is replaced with the recipient's first name.
      inviteTemplates: {
        till: (name) => `Hi ${name} — I've been praying for you. Wanted to share Ignite with you; it's been helping me grow. No pressure to do anything with it, just take a look when you have a minute.`,
        soil: (name) => `${name}, you've been on my heart lately. I use Ignite for prayer and growth — wanted to invite you to see it. Would love to hear what you think.`,
        seed: (name) => `${name} — we've been talking about faith stuff and I wanted to share Ignite with you. It's been a really helpful way for me to dig into Scripture. Want to take a look together?`,
        sprout: (name) => `${name}! So glad you're walking with Jesus. Ignite has been huge for me in building daily prayer and Bible rhythms. Come grow alongside us.`,
        plant: (name) => `${name} — I think you'd really appreciate Ignite. Great place to grow, build prayer habits, and walk with others on the journey.`,
        harvest: (name) => `${name} — sharing Ignite with you. It's been a great tool for me in praying for and discipling the people God has put in my life. Excited to see how you use it.`,
      },
      // Invite-outcome status shown on the recipient card.
      inviteStatus: {
        justSent: 'Invite sent — just now',
        sent: (days) => `Invited ${days}d ago — not yet claimed`,
        sentWithClicks: (days, clicks) => `Invited ${days}d ago — opened ${clicks}×`,
        claimedJustNow: 'Just joined via your invite',
        claimed: (days) => `Joined ${days}d ago via your invite`,
      },
      nextStepsTitle: 'A next step you could take',
      nextSteps: {
        till: [
          'Pray for them by name — out loud and specific.',
          'Do a small, no-strings act of service this week.',
          'Reach out with no agenda — just show up.',
          'Drop a quick "thinking of you" text.',
          'Ask God to soften the ground; don\'t force a conversation.',
          'Look for one practical need you could quietly meet.',
        ],
        soil: [
          'Ask about their week — listen, don\'t fix.',
          'Invite them to coffee or a meal.',
          'Share a piece of your story when it fits naturally.',
          'Ask a deeper question and stay in the room for the answer.',
          'Invite them into your life — a meal at your table, a service project.',
          'Notice something God is doing in them; name it out loud.',
        ],
        seed: [
          'Invite them to read a passage of Scripture with you.',
          'Start a Discovery Bible Study around one passage.',
          'Ask if they\'d like to pray with you about something they\'re facing.',
          'Share the Gospel clearly when the moment opens.',
          'Bring them along to something where the Word will be opened.',
          'Ask: "Where would you say you are with Jesus right now?"',
        ],
        sprout: [
          'Talk through baptism if they haven\'t been baptized.',
          'Help them build a daily Bible + prayer rhythm.',
          'Pray together — and let them pray out loud.',
          'Ask what God has been showing them this week.',
          'Walk them through what it means to belong to a body of believers.',
          'Help them write down their testimony in three short paragraphs.',
        ],
        plant: [
          'Ask who in their life they\'re praying for and pursuing.',
          'Equip them to share their testimony out loud.',
          'Hand off a piece of ministry — let them lead something.',
          'Read a book of the Bible together and discuss it weekly.',
          'Talk through an area of obedience they\'re wrestling with.',
          'Pray together by name for someone they\'re reaching.',
        ],
        harvest: [
          'Hear about the people they\'re discipling.',
          'Pray for their disciples by name.',
          'Encourage them — multiplication is heavy and lonely.',
          'Ask where they\'re stuck and coach gently.',
          'Celebrate a specific fruit you see in their life.',
          'Pray over their next handoff and release.',
        ],
      },
    },
    calendarTab:'Calendar', recipientsTab:'Recipients',
    viewCalendar:'View Calendar →', addPerson:'Add Person',
    addFirstRecipient:'Add First Recipient', tryAgain:'Try Again',
    // Prayer cadence, not a ranking. The stored values are still
    // normal|high|urgent, but nobody should read "Urgent" next to a person's
    // face — we show how often we pray for them instead.
    // How often someone comes up on your prayer calendar. Stored values are still
    // normal|high|urgent, and the scheduler still reads those — but nobody should
    // see a person ranked "Urgent" next to their face. The descriptions explain
    // the mechanism the labels hide, which is the whole reason they exist.
    priorities:{ normal:'Regular Rotation', high:'Weekly', urgent:'Daily',
      normalDesc:'One person a day, working through your list',
      highDesc:'Pray Every Week', urgentDesc:'Pray Every Day' },
    faith:{ yes:'Yes', no:'No', unknown:"I don't Know", na:'N/A' },
    faithChips:{ yes:'Has Faith', no:'No Faith', unknown:'Unknown' },
    relationshipGroups:{
      immediate:'Immediate Family', extended:'Extended Family', friends:'Friends, Neighbors & Strangers',
    },
    relationships:[
      'Husband','Wife','Fiancé','Mother','Step Mother','Father','Step Father',
      'Brother','Step Brother','Sister','Step Sister','Son','Step Son','Daughter','Step Daughter',
      'Cousin','Uncle','Aunt','Niece','Nephew','Grandson','Granddaughter',
      'Grandmother','Grandfather','Mother-in-law','Father-in-law','Brother-in-law',
      'Sister-in-law','Son-in-law','Daughter-in-law',
      'Boyfriend','Girlfriend','Best Friend','Close Friend','Friend',
      'Neighbor','Employee','Employer','Co-worker','Acquaintance',
      'Stranger','Church Family','Classmate','Pastor','Mentor','Mentee',
    ],
    months:['January','February','March','April','May','June','July','August','September','October','November','December'],
    days:['Sun','Mon','Tue','Wed','Thu','Fri','Sat'],
    formType:'Type', formIgniteMember:'Ignite Member',
    formName:'Name', formNamePlaceholder:'Full name',
    // The field means "how often do you pray for them", so it says so. The key
    // keeps its old name to avoid churning every call site for a label change.
    formPriority:'Prayer Cadence', formRelationship:'Relationship',
    formSelectRelationship:'Select relationship…',
    formFaith:'Do they have faith?',
    formRequest:'Prayer Request / Notes',
    formRequestPlaceholder:'What are you praying for them?',
    searchMembers:'Search Ignite members…',
    editRecipient:'Edit Recipient', addRecipient:'Add Prayer Recipient',
    nameRequired:'Name is required', selectMember:'Please select an Ignite member',
    failedSave:'Failed to save',
    prayFor:'Pray for', myNotes:'My Notes', addNotes:'+ Add notes…', editNotes:'Edit', editingMyNotes:'Editing My Notes',
    viewWeek:'Week', viewMonth:'Month', todayBtn:'Today',
    statsAllPrayed:'All prayed for today',
    statsStillToday:(n)=>`${n} ${n===1?'prayer':'prayers'} still today`,
    prayNowBtn:'Pray now',
    motivStart:'Pray today to start a new streak.',
    motivBeatBest:(d,best)=>`${d} more ${d===1?'day':'days'} to beat your best streak of ${best}!`,
    motivNewRecord:'You are on your longest streak ever — keep it going!',
    motivGoingStrong:(n)=>`${n} days strong. Keep the chain alive!`,
    motivAllDone:'Everyone is lifted up today. Well done.',
    motivAllDoneStreak:'Everyone lifted up — and your longest streak yet!',
    myRelationship:(rel)=>`My ${rel}`,
    prayedToday:'Prayed Today', prayButton:'I Prayed!', prayTodayMenu:'Pray Today', prayingButton:'Praying…', prayedButton:'Prayed!',
    samplePrayer:'Sample Prayer', generatePrayer:'Generate Sample Prayer',
    generatingPrayer:'Generating…', noPrayersDay:'No prayers scheduled for this day.',
    couldNotSaveNotes:'Could not save notes', couldNotMarkPrayed:'Could not mark as prayed',
    couldNotGenerate:'Could not generate prayer',
    wantToBrighten:"Want to brighten their day?",
    nudges:(name)=>[
      `"Hi ${name} — you came up on my prayer calendar today. Anything specific I can pray for you this week?"`,
      `"Hey ${name}, just wanted you to know I prayed for you today. How are you doing?"`,
      `"${name}, you were on my heart today — I lifted you up in prayer. Hope you're doing well!"`,
      `"Hi ${name}, thinking of you today and saying a prayer. Any requests I can bring to God for you?"`,
      `"Hey ${name} — you came to mind during my prayer time. Just wanted you to know you're being prayed for ❤️"`,
      `"${name}, I prayed for you today! Would love to know how I can be more specific in praying for you."`,
    ],
    addBtn:'Add',
    calMyRequest:'My Request',
    yourPrayerRequest:'Your prayer request', fromNetwork:(name)=>`From ${name || 'your network'}`,
    prayerRequestLabel:'Prayer request',
    activeLabel:'Active', answeredLabel:'Answered',
    activeToday:'Active today', activeYesterday:'Active yesterday', activeDaysAgo:(d)=>`Active ${d}d ago`,
    addToCalendar:'Pray on Specific Day', addingToCalendar:'Adding…',
    addToCalendarShort:'Calendar', addedToCalendarShort:'Added',
    prayToday:'Pray today', pickADay:'Pick a day…', praySpecificDay:'Pray on Specific Day', removeFromList:'Remove from List',
    addUpdate:'Add Update', updatesLabel:(n)=>`${n} Update${n!==1?'s':''}`, updatePlaceholder:'Share a prayer update…', showMoreUpdates:(n)=>`Show ${n} more update${n!==1?'s':''}`,
    copyPrayer:'Copy', sharePrayer:'Share', newPrayer:'New',
    previousPrayer:'Previous prayer', nextPrayer:'Next prayer',
    discoverTitle:'Discover', requestsTitle:'Requests',
    discoverEmpty:'You\'re all caught up', discoverEmptyDesc:'No new prayer requests from your network right now.',
    discoverReviewBtn:'Revisit passed requests', discoverReviewMode:'Revisiting passed requests',
    discoverReviewEmpty:'You\'ve revisited them all', discoverReviewEmptyDesc:'Nothing more to revisit right now.',
    addPrayerRequest:'Add a prayer request',
    myRequests:'My Requests', prayingFor:'Praying For',
    youPrayed:(n)=>`You prayed ${n}×`,
    godAnswered:'God answered!', godAnsweredTitle:'God answered this prayer', editRequest:'Edit', deleteRequest:'Delete', reportRequest:'Report',
    yourPrayerListEmpty:'Your prayer list is empty',
    prayerListEmptyDesc:'Swipe right on requests in the Discover tab to add them here.',
    quickAddRecipients:'Quick Add Recipients', addMultiplePeople:'Add multiple people at once',
    showMore:'Show more', showLess:'Show less',
    addToCalendarDone:'Added to calendar', addToCalendarBtn:'Add to calendar',
    pickDateAdd:'Add',
    chooseDate:'Please choose a date.',
    addedToday:'Added to your Prayer Calendar for today.',
    addedDate:'Added to your Prayer Calendar.', couldNotAdd:'Could not add to calendar',
    showMore:'Show more', showLess:'Show less',
    removeConfirm:'Remove this prayer recipient?',
    noPrayerRecipients:'No prayer recipients yet', noneInFilter:'None in this filter',
    addPeoplePrompt:'Add people you want to pray for.',
    tryDifferentFilter:'Try a different filter.',
    noRecipientsYet:'Add people to pray for and your calendar builds automatically.',
    failedToLoad:'Could not load prayer data. Please try again.',
    memorial: {
      toggle: 'This person has passed away',
      toggleSub: 'Switch to memorial prayer mode',
      convertBtn: 'Mark as passed away',
      passedOnLabel: 'Date of Passing',
      passedOnOptional: '(optional)',
      frequencyLabel: 'How often to remember in prayer',
      frequencyOptions: {
        anniversaries: 'On anniversaries only',
        anniversariesDesc: 'Date of passing · All Saints Day · Christmas · Easter',
        monthly: 'Once a month',
        monthlyDesc: 'On the same day each month',
        quarterly: 'Quarterly',
        quarterlyDesc: 'January, April, July & October',
      },
      faithLegacyLabel: 'Their faith legacy',
      faithLegacyPlaceholder: 'One sentence about their walk with God — e.g. "She prayed over me every morning"',
      faithLegacyOptional: '(optional)',
      calEventLabel: 'Remembering',
      calEventSub: 'Memorial prayer',
      detailTitle: (name) => `Remembering ${name}`,
      detailSub: 'In gratitude for their life and faith',
      prayedBtn: 'I Remembered them in Prayer',
      recipientTag: 'Memorial',
      anniversaryTag: 'Anniversary',
      allSaintsTag: "All Saints' Day",
      christmasTag: 'Christmas',
      easterTag: 'Easter',
      monthlyTag: 'Monthly Remembrance',
      quarterlyTag: 'Quarterly Remembrance',
    },
  },

  bibleStudy: {
    title:'Bible Study',
    subtitle:'Let the Word search you',
    newStudy:'New Study', currentStudy:'Current Study', pastStudies:'Past Studies',
    searchBooks:'Search books…',
    oldTestament:'Old Testament', newTestament:'New Testament',
    noMatch:(q)=>`No books match "${q}".`,
    chaptersHint:'Tap chapters to select them.',
    chaptersSelected:(n,chs)=>`${n} chapter${n>1?'s':''} selected: ${chs}`,
    continueToVerses:'Continue to Verses →',
    tapOrDrag:'Tap or drag across verses to select them. You can select across multiple chapters.',
    dragHint:'Tap a verse to toggle it · Hold and drag to select a range',
    chapter:(n)=>`Chapter ${n}`,
    tapToSelect:'tap to select verses',
    versesSelected:(n)=>`${n} verse${n>1?'s':''} selected`,
    diveIn:'Dive into Bible Study',
    generate:'Generate Study', generating:'Generating…',
    preparingStudy:'Preparing your study…',
    aiStudying:'Ignite AI is studying the passage — this takes a few seconds.',
    saveAnswers:'Save My Answers',
    saving:'Saving…', savedLabel:'Answers saved',
    background:'Background & Context',
    readInTranslations:'📖 Read the passage in at least 2 different translations',
    translationsNote:(ref)=>`We encourage you to read ${ref} in multiple translations before answering the questions below. Each translation offers a unique perspective.`,
    loadingPassage:'Loading passage\u2026',
    aboutGod:'What does this passage tell us about God and/or Jesus?',
    aboutHumans:'What does this passage tell us about humans?',
    howRespond:'How might God be inviting you to respond to this passage?',
    sharePrompt:'💬 Is there someone you could encourage by sharing this passage?',
    shareHint:'Think of someone who might be blessed by these verses. Who comes to mind?',
    shareAnswerPlaceholder:'Who came to mind? You could note their name or jot a thought about reaching out…',
    thinkFirst:"Think about this question for yourself first, then tap to reveal Ignite AI's thoughts.",
    yourAnswer:'Your answer (private)…',
    personalReflection:'Your personal reflection (private)',
    newStudyBtn:'← New Study',
    bibleStudyLabel:'Bible Study',
    savedStudies:(n)=>`${n} saved ${n===1?'study':'studies'} — tap to review and continue reflecting.`,
    noStudiesYet:'No Bible studies yet',
    noStudiesDesc:'Start your first study above and it will appear here for future reference.',
    startStudy:'Start a Study',
    loadingPast:'Loading past studies…',
    noStudies:'No past studies yet.', delete:'Delete',
    deleteConfirm:'Delete this Bible study? This cannot be undone.',
    inspireMe:'Inspire Me',
    inspiring:'Choosing a passage…',
    failedGenerate:'Failed to generate Bible study',
    failedSave:'Failed to save', failedLoad:'Failed to load study', failedDelete:'Failed to delete',
    lessons:(n)=>`${n} lessons`, done:'done',
    complete:'Complete', back:'Back', lessons2:'Lessons', by:'by',
    selectBook:'Select a book of the Bible to begin your study.',
    selectChapters:(book)=>`Select one or more chapters in ${book}. You can choose multiple to pick verses across them.`,
    chapterAbbr:(n)=>`Ch. ${n}`,
    chaptersAbbr:(n)=>`${n} chapters`,
    // ── Sharing ──
    shareStudyBtn:'Share',
    shareTitle:'Share this study',
    shareWhoCanView:'Who can view',
    shareVisPublic:'Anyone with the link',
    shareVisPublicDesc:'Works for anyone, even without an account.',
    shareVisConnections:'Connections only',
    shareVisConnectionsDesc:'Only your connections, signed in, can open it.',
    shareIncludeAnswers:'Include my reflection answers',
    shareIncludeAnswersHint:'Off by default — your private notes stay private unless you turn this on.',
    shareCreateLink:'Create link',
    shareCopy:'Copy',
    shareCopied:'Copied',
    shareUpdate:'Update',
    shareUpdateHint:'Changed the options above? Tap Update to apply them to the link.',
    shareSendMessage:'Send in a message',
    shareStop:'Stop sharing',
    shareCancel:'Cancel',
    shareBack:'Back',
    shareSendSub:'Send this study to a connection in a direct message.',
    shareSearchConnections:'Search connections…',
    shareNoConnections:'No connections yet.',
    shareDmNotePlaceholder:'Add a note… (optional)',
    shareSentTo:(name)=>`Sent to ${name}`,
    shareFailed:'Something went wrong. Please try again.',
    sharedBy:(name)=>`Shared by ${name}`,
    sharedReflectionLabel:(name)=>`${name}'s reflection`,
    shareNotFoundTitle:'Study not found',
    shareNotFoundBody:'This study may have been removed or made private.',
    shareForbiddenTitle:'Sign in to view',
    shareForbiddenBody:'This study is shared with the author’s connections. Sign in to see if you have access.',
    shareSignIn:'Sign in',
    shareJoinCta:'Join Ignite',
    shareLearnMore:'Learn more about Ignite',
  },

  communities: {
    title:'Communities', subtitle:'Walk together in faith', addBtn:'Add',
    searchPlaceholder:'Search communities…',
    tabs:{ mine:'Mine', pending:'Pending', discover:'Discover' },
    types:{
      church:'Church', ministry:'Ministry', missional:'Missional / Outreach',
      small_group:'Small Group', bible_study:'Bible Study',
      prayer_group:'Prayer Group', recovery:'Recovery',
      family_friends:'Family & Friends',
      interests_hobbies:'Interests & Hobbies',
      // Legacy labels — kept so existing communities still display a name
      neighborhood:'Neighborhood', friend:'Friend Group', family:'Family',
      volunteer:'Volunteer',
    },
    visibility:{
      public:'Public', private:'Private', hidden:'Hidden',
      publicDesc:'Anyone can see & join',
      privateDesc:'Visible but must request to join',
      hiddenDesc:'Invite-only, not discoverable',
    },
    join:'Join', leave:'Leave', open:'Open',
    requestToJoin:'Request to Join', requested:'Requested',
    noCommunities:'No communities yet',
    joinOrCreate:'Join a community or create your own.',
    browse:'Browse Communities',
    nothingToDiscover:'Nothing to discover',
    featured:'Featured',
    createYourOwn:'Create a new community for your group.',
    activeToday:'Active today', activeYesterday:'Active yesterday', activeDaysAgo:(d)=>`Active ${d}d ago`,
    failedLoad:'Could not load communities',
    somethingWrong:'Something went wrong',
    createModal:{
      title:'Create a Community',
      // Third step used to be a "Done" placeholder that never rendered — the
      // wizard submitted at the end of step 2. It is a real step now.
      steps:['Basics','Privacy','Features'],
      featuresTitle:'What will this community use?',
      featuresHelp:'You can change any of this later in Community Features.',
      nameLabel:'Community Name *', namePlaceholder:'e.g. Sunday Morning Prayer Circle',
      descLabel:'Description', descPlaceholder:'What is this community about?',
      typeLabel:'Type',
      parentLabel:'Parent Community', parentOptional:'— optional',
      parentSearch:'Search your communities…',
      nameRequired:'Name is required', failedCreate:'Failed to create',
      createBtn:'Create Community 🎉',
      subCommunity:'Sub-community',
    },
  },

  community: {
    tabs:{ home:'Home', feed:'Feed', activity:'Activity', bible:'Bible Reading Plan', growth:'Growth', calendar:'Calendar', members:'Members', chat:'Chat', sub:'Sub-Communities', invites:'Invites', manage:'Manage', leave:'Leave', rhythm:'Daily Rhythm', features:'Community Features' },

    // Organizer feature switches (/communities/:id/features). Copy here has one
    // job beyond naming the feature: say what members lose when it goes off.
    // These are not preferences — every one of them removes a surface from
    // everybody in the community, so the hint carries the consequence.
    features: {
      title:    'Community Features',
      subtitle: 'Choose what this community offers. Anything switched off disappears for members — nothing is deleted, and turning it back on brings it straight back.',
      savedNote:'Changes apply to everyone immediately.',
      calendar: {
        label: 'Calendar',
        hint:  'Events, the week strip on Home, and the upcoming events list.',
        disableConfirm: 'Turn off the Calendar? Members will no longer see events or the week strip, and the Zoom room will be hidden with it. Your events are kept.',
      },
      biblePlans: {
        label: 'Bible Reading Plans',
        hint:  'Shared reading plans with daily readings and reflection questions.',
        disableConfirm: 'Turn off Bible Reading Plans? Members will lose access to every plan in this community until you turn it back on. Their own reflections stay in their Journal.',
      },
      growth: {
        label: 'Growth Plans',
        hint:  'The shared view of growth plans members are doing together here.',
        disableConfirm: 'Turn off Growth Plans? Members keep every plan they are enrolled in — only this community’s shared view of them goes away.',
      },
      zoom: {
        label:        'Zoom room',
        hint:         'A recurring Zoom meeting for this community, shown on the Calendar.',
        requiresCalendar: 'Turn the Calendar on to use a Zoom room.',
        disableConfirm:   'Turn off the Zoom room? The join link stops being shown to members. The meeting itself is kept — use Remove room below to delete it for good.',
      },
    },
    switcher:{
      label:'Switch community',
      exploreMore:'Explore',
      exploreAria:'Explore more communities',
    },
    home:{
      happeningNow:'Happening now',
      serviceStartingIn:(mins)=>`Service starts in ${mins} min`,
      serviceLive:'Live now — join the room',
      joinNow:'Join Now',
      todayReadingDue:"Today's reading is ready",
      eventStartsIn:(label, dur)=>`${label} in ${dur}`,
      todayReading:"Today's reading",
      dayOfN:(day, total)=>`Day ${day} of ${total}`,
      noActivePlan:'No reading plan yet',
      startReadingPlan:'Start a reading plan',
      readingProgress:'Your progress',
      thisWeek:'This week',
      nextEvent:'Next event',
      noEventsThisWeek:'No events this week',
      addFirstEvent:'Add an event',
      growthPlan:'Growth plan',
      sessionOfN:(s, total)=>`Session ${s} of ${total}`,
      membersInPlan:(n)=> n===1 ? '1 member in this plan' : `${n} members in this plan`,
      noGrowthPlan:'No growth plan yet',
      addGrowthPlan:'Start a growth plan',
      theWell:'The Well',
      latestSermon:'Latest sermon thread',
      repliesCount:(n)=> n===1 ? '1 reply' : `${n} replies`,
      noWellThreads:'No sermon threads yet',
      addWellThread:'Start a thread',
      subCommunities:'Sub-communities',
      recentActivity:'Recent activity',
      seeAll:'See all',
      noRecentPosts:'Be the first to share',
      membersTitle:'Members',
      upcomingEvents:'Upcoming events',
      organizerTools:'Organizer tools',
      viewAll:'View all',
      more:'More',
      overflowMenuTitle:'Community menu',
      comingSoon:'Coming soon',
      viewAllPlans:'View all reading plans',
      viewAllGrowthPlans:'View all growth plans',
      viewAllThreads:'View all threads',
      personOne:'person',
      personMany:'people',
      shareWithCommunity:'Share with the community…',
      // Bible card "next unit" prompt
      nextUp:'Next up for you',
      planComplete:"You've finished this plan!",
      planNotStarted:"This plan hasn't started yet.",
      tapToRead:'Tap to read',
      // Well card subtitle
      wellSubtitle:"Continue the conversation around Sunday's sermon",
      growthPlanSubtitle:'Grow together with your community',
      todayReadingSubtitle:'Stay in step with your community',
      recentActivitySubtitle:"What's on everyone's heart",
      // Non-member landing
      joinToSee:'Join to see what\'s inside',
      joinToSeeSubtitle:'Reading plans, growth plans, sermon discussions, and the community feed are visible to members.',
      joinToSeePrivateSubtitle:'This is a private community. Members get access to reading plans, growth plans, sermon discussions, and the community feed.',
      joinPromptBody:'Use the button at the top to join (or request to join) this community and unlock all of its content.',
    },
    join:'Join', leave:'Leave', request:'Request', pending:'Pending',
    leaveConfirm:'Leave this community?',
    leaveTab:{
      title:'Leave Community',
      btn:'Leave Community',
      publicNote:'You can rejoin at any time.',
      privateNote:'This is a private community. You will need to request permission to join again.',
      hiddenNote:'This is a hidden community. You will no longer be able to see it, and will need a direct invite to rejoin.',
    },
    parentCommunity:'Parent Community',
    parentPlaceholder:'Select a community (optional)',
    parentNone:'None',
    deleteConfirm:(name)=>`Delete "${name}"? This cannot be undone.`,
    postTypes:{
      reflection:'Reflection', scripture:'Scripture', prayer:'Prayer', praise:'Praise', surrender:'Surrender',
      prayer_request:'Prayer Request',
    },
    placeholders:{
      reflection:"What's on your heart today?",
      scripture:'Share a verse that spoke to you…',
      prayer:'What would you like prayer for?',
      praise:'Share something worth celebrating…',
      surrender:'What are you surrendering to God today?',
    },
    sharePlaceholder:'Share with the community…',
    writeSomething:'Please write something first', failedPost:'Failed to post',
    sharedWithComm:'Shared with community! ✨',
    noPostsYet:'No posts yet', beFirstComm:'Be the first to share!',
    shareModalTitle:'Share with Community',
    postingTo:'Posting to this community only',
    deletePost:'Delete this post?',
    zoomRoom:'Community Zoom Room', zoomAvailable:'Always available — join anytime',
    zoomPasscode:(code)=>`Passcode: ${code}`, joinNow:'Join Now',
    upcomingEvents:'Upcoming Events',
    noUpcomingEvents:'No upcoming events', addEventPrompt:'Add events for your community.',
    addEvent:'+ Add Event',
    days:['Sun','Mon','Tue','Wed','Thu','Fri','Sat'],
    repeats:(r)=>`Repeats ${r}`,
    eventModal:{
      addTitle:'Add Event', editTitle:'Edit Event',
      titleLabel:'Title *', titlePlaceholder:'e.g. Sunday Service',
      descLabel:'Description', locationLabel:'Location',
      locationPlaceholder:'e.g. Main Hall or Zoom link',
      startLabel:'Start *', endLabel:'End',
      repeatsLabel:'Repeats', onDaysLabel:'On days',
      repeatOptions:{ none:'Does not repeat', daily:'Daily', weekly:'Weekly', biweekly:'Bi-weekly', monthly:'Monthly' },
      addBtn:'Add Event', editBtn:'Update Event',
    },
    roles:{ organizer:'Organizer', co_organizer:'Co-Organizer', member:'Member' },
    removeFromComm:'Remove this member?',
    joinRequests:(n)=>`Join Requests (${n})`,
    membersCount:(n)=>`Members (${n})`,
    accept:'Accept',
    chatPlaceholder:'Message the community…',
    chatMsgPlaceholder:'Message…',
    noMessages:'No messages yet',
    beFirstChat:'Be the first to say something!',
    noSubComms:'No sub-communities',
    noSubCommsDesc:'Create a sub-community when you need a focused group within this one.',
    searchInvite:'Search members to invite…',
    sentInvites:(n)=>`Sent Invites (${n})`,
    noInvites:'No invites yet',
    noInvitesDesc:'Invite people above to grow your community.',
    invite:'Invite',
    manage:{
      title:'Manage', typeLabel:'Type', visibilityLabel:'Visibility',
      communityName:'Community Name', description:'Description',
      communityInfo:'Community Info',
      visibilityOptions:{
        public:'Public — anyone can see & join',
        private:'Private — anyone can see, request to join',
        hidden:'Hidden — invite only, not listed',
      },
      saveChanges:'Save Changes',
      changeCover:'Change Cover', addCover:'Add Cover Photo',
      zoomSection:'Zoom Room', joinLink:'Join Link', hostLink:'Host Link',
      openHost:'Open as host →', passcodeLabel:'Passcode',
      replace:'Replace Room', removeZoom:'Remove',
      noRoom:'No Zoom room assigned. Create one so members always have a meeting link ready.',
      createRoom:'Create Zoom Room',
      replaceConfirm:'This will replace the existing Zoom room with a new one. Members using the old link will need the new one. Continue?',
      removeConfirm:'Remove the Zoom room from this community? Members will lose access to the link.',
      zoomRemoved:'Zoom room removed.',
      dangerZone:'Danger Zone', deleteCommunity:'Delete This Community',
    },
    biblePlan:{
      allPlans:'All Plans', editPlan:'Edit Plan', download:'Download', editDay:'Edit Day',
      editDayModal:{title:(n)=>`Edit Day ${n}`,titleOptional:'Day Title (optional)',dayTitle:'e.g. Walking in Faith',verses:'Scripture Verses',versesPlaceholder:'e.g. John 3:16-18',commentary:'Commentary / Devotional',commentaryLabel:'Commentary',commentaryPlaceholder:'Brief devotional notes for this day...',questions:'Reflection Questions',addQuestion:'Add question',saveChanges:'Save Changes'},
      createReadingPlan:'Create Reading Plan',
      days:(n)=>`${n} days`,
      noDaysYet:'No days yet',
      noDaysDesc:'Organizers can add reading days.',
      noPlansOrg:'Create a Bible reading plan for your community.',
      noPlansMember:'Ask an organizer to create a reading plan.',
      noPlans:'No reading plans yet.',
      addDay:(uw)=>`+ Add ${uw}`,
      unitWord:'Day', unitWordWeek:'Week', unitWordReading:'Reading',
      weekNum:(n)=>`Week ${n}`,
      readingNum:(n)=>`Reading ${n}`,
      thisWeek:'This Week',
      weekProgress:(done,total)=>`${done} of ${total} done`,
      noReadingsThisWeek:'No readings yet — add some.',
      noReadingsYet:'No readings yet for this week.',
      lockedTitle:'Reading locked',
      lockedSub:(range)=>`Available ${range}.`,
      startsOn:(date)=>`Starts ${date}`,
      notStartedYet:'The plan begins on its start date — readings unlock then.',
      planEnded:'Plan ended',
      createNextYear:'Create a new plan to continue next year.',
      readingPlanLabel:'Reading Plan',
      scripturePassage:'Scripture Passage *',
      addDayBtn:(uw)=>`Add ${uw}`,
      activeBadge:'Active', draftBadge:'Draft',
    planCategory: 'Category',
    planRating: 'Content Rating',
    oakdaleCore4Label: 'Always include Oakdale Core 5 Questions',
    oakdaleCore4Desc: 'AI will always include the 5 standard reflection questions, then add 1–3 more.',
    aiAutoFillLabel: 'Fill empty fields with AI',
    aiAutoFillDesc: 'A background helper will fill any blank Day Title or Commentary with biblical, plan-specific writing — usually within a few minutes.',
    narratorVoice: 'Narrator Voice',
    narratorVoiceDesc: 'Voice used when generating audio for this plan\'s units.',
    voiceFemale: 'Female',
    voiceMale: 'Male',
    voiceBoth: 'Both',
    publishedLabel: 'Published',
    publishedDesc: 'Visible to enrolled users',
    unpublishedDesc: 'Hidden — only visible to you',
    categories: {
      faith_foundations:    'Faith Foundations',
      universal_human:      "I'm Just Human",
      battles_against_sin:  'Battles Against Sin',
      grief_and_loss:       'Grief & Loss',
      real_world:           'Real-World Situations',
      discipleship:         'Discipleship',
      church_community:     'Church & Community',
    },
    ratings: {
      everyone:            '✅ Everyone',
      everyoneDesc:        'Suitable for all ages',
      guidance_suggested:  '👁 Parental Guidance',
      guidanceDesc:        'Some mature themes — appropriate for teens and adults',
      mature:              '🔞 Mature',
      matureDesc:          'Addresses explicit sin, addiction, sexual integrity, or deep grief — adults only',
    },
      brpDayShort:'day', brpWeekShort:'wk',
      publishConfirm:'Publish this plan? Members will be notified.',
      backToPlan2:'Back to Plan',
      communityProgressLabel:'Community Progress',
      editCoverBtn:'Edit Cover',
      repositionBtn:'Reposition',
      planCategory: 'Category', planRating: 'Content Rating',
    oakdaleCore4Label: 'Always include Oakdale Core 5 Questions',
    oakdaleCore4Desc: 'AI will always include the 5 standard reflection questions, then add 1–3 more.',
    narratorVoice: 'Narrator Voice',
    narratorVoiceDesc: 'Voice used when generating audio for this plan\'s units.',
    voiceFemale: 'Female',
    voiceMale: 'Male',
    voiceBoth: 'Both',
    publishedLabel: 'Published', publishedDesc: 'Visible to enrolled users',
    unpublishedDesc: 'Hidden — only visible to you',
    loadingPassage:'Loading passage…',
      addCustomQuestion:'Add custom question',
      commentaryLabel:'Commentary',
      readMultipleTranslations:'Read in Multiple Translations',
      alwaysIncluded:'Always included',
      chooseAnswersToShare:'Choose which answers to share with your community. Scripture is always included.',
      postToCommunity:'Post to Community',
      shareReflectionTitle:'Share Reflection',
      noActivityYet:'No activity yet',
      noActivityDesc:(uw)=>`Complete a ${uw} and share your reflection to get started.`,
      noReflectionsYet:'No shared reflections yet',
      noReflectionsDesc:(uw)=>`Tap "Share with Community" when completing a ${uw}.`,
      progressStages:['Just getting started','Growing in faith','Building together','Strong community','Almost there!','Community complete!'],
      progressSub:'tap to see reflections',
      beFirstComplete:(uw)=>`Be the first — complete a ${uw} to get the community started`,
      updateBtn:'Update',
      cancelBtn:'Cancel',
      publishBtn:'Publish',
      downloadFailed:'Download failed',
      couldNotPublish:'Could not publish',
      couldNotShare:'Could not share',
      uploadingMedia:'Uploading…',
      photoUploadFailed:'Photo upload failed',
      coverUpdateFailed:'Cover update failed',
      backToAllPlans:'All Plans',
      dayNum:(n)=>`Day ${n}`,
      reflectionQuestions:'Reflection Questions',
      yourThoughts:'Your thoughts…',
      addCustomQuestion:'+ Add custom question',
      backToPlan:'← Back to Plan',
      savePrivately:'Save Privately',
      shareWithComm:'Share with Community',
      createPlan:'Create Plan',
      titleLabel:'Plan Title *', aiGenerate:'AI Generate', aiGenerating:'Generating…',
      createModal:{
        title:'Create Reading Plan',
        titleLabel:'Plan Title *', titlePlaceholder:'e.g. 30 Days in the Psalms',
        descLabel:'Description', descPlaceholder:'What will your community be reading?',
        totalDays:'Total Days',
        addManually:'Add Days Manually',
        createWithAI:'Create with AI ✨',
        aiDesc:(n)=>`AI generates all ${n} days with verses, devotionals & questions tailored to your community.`,
        aiStatus:'Asking AI to craft your reading plan…',
        saving:(n)=>`Saving plan with ${n} days…`,
        aiFailed:'AI generation failed. Please try again.',
      },
      weekStart:{
        label:'Start date (first day of Week 1)',
        shape:(from, to)=>`Weeks run ${from} → ${to}`,
        week1:(range)=>`Week 1: ${range}`,
        lastWeek:(n, range)=>`Week ${n}: ${range}`,
        hint:'Any day works. Meeting on Sundays? Start on the Monday before, so your meeting falls on the last day of the week you discuss and the next week opens the following morning.',
      },
      editModal:{
        title:'Edit Reading Plan',
        titleLabel:'Plan Title', descLabel:'Description', totalDays:'Total Days',
        saveChanges:'Save Changes',
        regenerate:'Regenerate with AI',
        regenerateNote:'Regenerate replaces all existing days with fresh AI-generated content.',
        deletePlan:'Delete This Plan',
        regenerateConfirm:(n)=>`Regenerate replaces all ${n} existing days with new AI content. All member responses will be lost. Continue?`,
        deleteConfirm:(title)=>`Delete "${title}"? All days and member responses will be lost.`,
        regenerating:'Generating new days with AI…',
        savingRegen:(n)=>`Saving ${n} regenerated days…`,
        regenFailed:'Regeneration failed. Please try again.',
        failedSave:'Failed to save', failedDelete:'Failed to delete',
      },
      addDayModal:{
        title:'Add Reading Day',
        dayNum:'Day #', dayTitle:'Day title',
        titleOptional:'Title (optional)',
        verses:'Verses *', versesPlaceholder:'e.g. Psalm 23:1-6, John 10:11',
        commentary:'Commentary / Devotional (optional)',
        commentaryPlaceholder:'Optional context or devotional thoughts…',
        questions:'Reflection Questions',
        addQuestion:'+ Add question',
        addDay:'Add Day',
        commentary:'Commentary / Devotional (optional)',
        questionPlaceholder:(n)=>`Question ${n}`,
      },
    },
    types:{
      church:'Church', ministry:'Ministry', missional:'Missional / Outreach',
      small_group:'Small Group', bible_study:'Bible Study',
      prayer_group:'Prayer Group', recovery:'Recovery',
      family_friends:'Family & Friends',
      interests_hobbies:'Interests & Hobbies',
      // Legacy labels — kept so existing communities still display a name
      neighborhood:'Neighborhood', friend:'Friend Group', family:'Family',
      volunteer:'Volunteer',
    },
    failedLoad:'Could not load community',
    chatBtn:'Chat',
    brpDayShort:'day', brpWeekShort:'wk',
    readingPlanLabel:'Reading Plan',
    visibilityLabels:{ public:'Public', private:'Private', hidden:'Hidden' },
    draftBadge:'Draft', activeBadge:'Active',
    backToCommunities:'Back',
    memberCount:(n)=>`${n} members`,
  },

  profile: {
    editCover:'Edit cover', setUsername:'+ Set username',
    joined:(date)=>`Joined ${date}`, edit:'Edit', connections:'Connections',
    save:'Save', cancel:'Cancel',
    firstName:'First Name', firstNamePlaceholder:'First name',
    lastName:'Last Name', lastNamePlaceholder:'Last name',
    username:'Username', bio:'Bio',
    bioPlaceholder:'Tell the community about yourself',
    favoriteVerse:'Favourite Verse', versePlaceholder:'e.g. Philippians 4:13',
    privateSettings:'🔒 Private Settings', privateNote:'Only visible to you',
    timezone:'Timezone', prayerReminder:'Daily prayer reminder',
    prayerReminderNote:"We'll email you who to pray for each day at this time.",
    noReminder:'No reminder', language:'Language', languageNote:'Changes the app language',
    stats:{
      postsShared:'Posts Shared', communities:'Communities', prayersSaid:'Prayers Said',
      connections:'Connections', prayerRecipients:'Prayer Recipients', prayerRequests:'Prayer Requests', currentStreak:'Current Streak',
      bestStreak:'Best Streak', daysActive:'Days Active', bibleStudies:'Bible Studies',
      growthCompleted:'Plans Completed', devices:'My Devices',
      prayerRate:'Prayer Rate',
    },
    recentActivity: 'My Activity',
    events: (n) => `${n} event${n === 1 ? '' : 's'}`,
    prev: '← Prev',
    next: 'Next →',
    justNow: 'just now',
    mAgo: (m) => `${m}m ago`,
    hAgo: (h) => `${h}h ago`,
    dAgo: (d) => `${d}d ago`,
    actLabels: {
      reflection: 'Reflection', scripture: 'Scripture', prayer: 'Prayer',
      praise: 'Praise', surrender: 'Surrender',
      prayer_prayed: 'Prayer Calendar', growth_unit: 'Growth Plan',
      bible_study: 'Bible Study', pray_now: 'Pray Now',
    },
    dayComplete: (n, title) => title ? `Day ${n}: ${title}` : `Day ${n} complete`,
    weekComplete: (n, title) => title ? `Week ${n}: ${title}` : `Week ${n} complete`,
    highlights: 'Spiritual Highlights',
    quickActions: 'Quick Actions',
    hlSub: {
      streak: 'Prayer calendar',
      growthCompleted: 'Grow Deeper',
      bibleStudies: 'Deep dives into Scripture',
      prayNowSessions: (n) => `Spoken prayer${n === 1 ? '' : 's'}`,
      daysActive: 'Member since day 1',
    },
    qaLabels: {
      prayNow: 'Pray Now', prayNowSub: 'Guided spoken prayer',
      addPrayer: 'Add Prayer Request', addPrayerSub: 'Your prayer requests',
      bibleStudy: 'Bible Study', bibleStudySub: 'Dive into Scripture',
      growDeeper: 'Grow Deeper', growDeeperSub: 'Growth plans',
    },
    streakDays: (n) => `${n}-day streak`,
    prayNowSessions: (n) => `${n} Pray Now session${n === 1 ? '' : 's'}`,
    planCount: (n) => `${n} plan${n === 1 ? '' : 's'} completed`,
    bibleCount: (n) => `${n} Bible stud${n === 1 ? 'y' : 'ies'}`,
    gettingStartedGuide: 'Getting Started Guide',
    signOut: 'Sign Out',
    dateOfBirth:'Date of Birth',
    dobDesc:'Used for age-appropriate content. Changing this recalculates your age group.',
    dobUnder13:'Ignite is for users 13 and older.',
    ageGroupLabel:'Age Group',
    ageGroupAdult:'Adult (18+)',
    ageGroupMinor:'Teen (13–17)',
    ageGroupUnset:'Not set',
    ageGroupDisplay:(group)=>group==='adult'?'Adult (18+)':group==='minor'?'Teen (13–17)':'Not set',
  },

  notifications: {
    title:'Notifications', unread:(n)=>`${n} unread`,
    markAllRead:'Mark all read', clearAll:'Clear all notifications?', clearAllBtn:'Clear all',
    empty:'All caught up!',
    emptyDesc:"No notifications yet. They'll appear here when someone mentions you, replies to your posts, or connects with you.",
    failedLoad:'Could not load notifications', somethingWrong:'Something went wrong',
    retry:'Retry',
  },

  members: {
    title:'Members', subtitle:'Find your people',
    searchPlaceholder:'Search by name or @username…',
    search:'Search', noFound:'No members found',
    noResults:(q)=>`No results for "${q}"`, noOthers:'No other members yet',
    removeConfirm:(name)=>`Remove ${name} as a connection?`,
    actionFailed:'Action failed',
    connect:'Connect', connected:'Connected', sent:'Sent', wantsToConnect:'Wants to Connect',
    viewProfile:'View Profile', posts:'posts', groups:'groups',
  },

  connections: {
    title:'Connections', subtitle:'Iron sharpens iron',
    tabs:{ connected:'Connections', pendingReceived:'Requests', pendingSent:'Sent' },
    noConnected:'No connections yet. Find members to connect with!',
    noPendingReceived:'No pending connection requests.',
    noPendingSent:'No sent requests.',
    nothingHere:'Nothing here', accept:'Accept', decline:'Decline',
    remove:'Remove', pending:'Pending…',
    removeConfirm:'Remove this connection?',
    actionFailed:'Action failed', findMembers:'Find Members',
  },

  courses: {
    title:'Grow Deeper', subtitle:'Rooted and built up in Him',
    growDeeperTitle:'Grow Deeper', growDeeperSubtitle:'Rooted and built up in Him',
    tabs:{ enrolled:'My Plans', explore:'Explore' },
    filterAll:'All Categories',
    filters: {
      category: 'Category',
      duration: 'Duration',
      maturity: 'Maturity',
      new: 'New',
      activeChallenge: 'Active Challenge',
      clearAll: 'Clear filters',
      days1_7: '1-7 Days',
      days8_14: '8-14 Days',
      days15_30: '15-30 Days',
      days31plus: '31+ Days',
      weeks1_4: '1-4 Weeks',
      weeks5_8: '5-8 Weeks',
      weeks9plus: '9+ Weeks',
      everyone: 'Everyone',
      guidanceSuggested: 'Guidance Suggested',
      mature: 'Mature',
      language: 'Language',
      langEnglish: 'English',
      langSpanish: 'Español',
      langAll: 'All languages',
    },
    startPlan:'Start this plan',
    unenroll:'Leave plan',
    unenrollConfirm:'Leave this plan? Your progress will be saved.',
    continueWhere:'Continue where you left off',
    byDay:'By Day (daily)', byWeek:'By Week (weekly)',
    cadenceDay:'day', cadenceWeek:'week', cadenceDays:'days', cadenceWeeks:'weeks',
    completed:'completed', enrolled:'Enrolled',
    commentary:'Commentary', reflectionQuestions:'Reflection Questions',
    commentaryCollapse:'collapse', commentaryRead:'read',
    commentaryReadMore:'… read more',
    audioCommentary:'Audio Commentary',
    narratorWoman:'Woman', narratorMan:'Man',
    narratorVoiceAria:'Narrator voice',
    narratorFemaleTitle:'Female narrator',
    narratorMaleTitle:'Male narrator',
    audioPlaybackSpeed:'Playback speed',
    audioBack15:'Back 15 seconds',
    audioForward15:'Forward 15 seconds',
    audioUnavailable:'Audio unavailable',
    audioReplay:'Replay',
    audioPause:'Pause',
    audioPlay:'Play commentary',
    invitationBegin:'Begin',
    preparingBlessing:'Preparing your blessing…',
    continueWithGod:'Continue your moment with God',
    prayAboutThisNow:'Pray about this now',
    prayAboutThisNowSub:'Open Pray Now with this passage pre-loaded',
    addPrayerRequestAction:'Add a prayer request',
    addPrayerRequestSub:'Take this reflection to your Prayer Requests',
    doneBackToPlan:'✓ Done — back to plan',
    reflectionTitlePrefix:(title)=>`Reflection: ${title}`,
    fromGrowDeeperPlan:(title)=>`From my Grow Deeper plan: ${title}`,
    writeFreelyPlaceholder:'Write freely — this is between you and God…',
    savePrivately:'Save Privately', shareWithCommunity:'Share with Community',
    writePlaceholder:'Write your reflection…',
    readOnBibleGateway:'Read on BibleGateway',
    download:'Download',
    sharePlan:'Share',
    reportPlan:'Report plan',
    linkCopied:'Link copied!',
    newPlan:'New plan', createFirst:'Create first plan',
    nothingHere:'Nothing here yet',
    noPlansAll:'Growth plans will appear here once created.',
    noPlansCategory:'No plans in this category.',
    noPlansSearch:'No plans match your search.',
    noResultsBuildDesc:'No existing plans match — but we can build one just for you.',
    noPlansExplore:'No plans to explore yet.',
    searchPlans:'Search plans…',
    noPlansEnrolled:'You haven\'t started any plans yet.',
    completedPlansHeader:(n)=>`Completed Plans (${n})`,
    allPlansCompleted:'All your plans are completed! Explore new ones.',
    createManually:'Create manually', createWithAI:'Create with AI',
    userCreateTitle:'Let\'s Build a Plan Together',
    userCreatePromptPlaceholder:'Describe what you\'re going through or what areas you feel like you could use some help...',
    userCreatePromptRequired:'Please describe what you would like a plan for.',
    userCreateBtn:'Let\'s Build It!',
    userCreateGenerating:'Creating your growth plan',
    genStepOutline:'Building outline',
    genStepContent:'Creating cover photo & writing commentary',
    genStepDone:'Done!',
    planTitle:'Plan title', planDescription:'Description',
    duration:'Duration', cadenceLabel:'Cadence',
    accentColor:'Accent color', generating:'Generating…',
    editPlan:'Edit plan', editUnit:'Edit unit',
    saveChanges:'Save changes', regenerateAI:'Regenerate AI',
    regenerateCover: 'Regenerate cover photo',
    regeneratingCover: 'Generating…',
    regenerateConfirm:(n,label)=>`Regenerate all ${n} ${label}s with AI? Existing units will be replaced.`,
    regenerateRebuildsSibling:'The translated version of this plan will also be rebuilt automatically — any reading progress on that side will be lost.',
    translationPending:'English version coming soon — usually ready within a few minutes.',
    langChipEn:'EN',
    langChipEs:'ES',
    viewTranslation:'View translation',
    showOriginal:'Show original',
    translating:'Translating…',
    translateRetry:'Translation failed — tap to retry',
    deletePlan:'Delete plan', deleteUnit:(label)=>`Delete ${label}?`,
    deleteConfirm:(title)=>`Delete "${title}"? This removes all units and responses.`,
    addUnit:(label)=>`Add ${label}`,
    addQuestion:'Add question',
    verses:'Bible verses', versesPlaceholder:'e.g. John 3:16-18',
    commentary2:'Commentary / devotional', commentaryPlaceholder:'2-3 paragraphs of devotional commentary…',
    questions:'Reflection questions',
    unitNum:(label,n)=>`${label} ${n}`,
    unitTitle:'Title', unitTitleOptional:'Title (optional)',
    challengeBadge:'Challenge', joinedBadge:'Joined', enrolledCount:(n)=>`${n} enrolled`, preparing:'Preparing your plan…',
    peopleDoingThis:(n)=>`${n} people are doing this with you`,
    journeyFeed:'Journey Feed', myJournal:'My Journal',
    communityFeed:'Community Feed',
    reflectionsWritten:(n)=>`${n} reflection${n===1?'':'s'} written`,
    journalEntry:(n)=>`${n} ${n===1?'entry':'entries'}`,
    completedOf:(done,total,label)=>`${done} of ${total} ${label} complete`,
    activeChallengeTitle:'Active Challenge',
    yourReflectionJournal:'Your Reflection Journal',
    words:'words',
    on:'On', off:'Off',
    startDateRequired:'Start date is required for challenges',
    planCategory: 'Category', planRating: 'Content Rating',
    oakdaleCore4Label: 'Always include Oakdale Core 5 Questions',
    oakdaleCore4Desc: 'AI will always include the 5 standard reflection questions, then add 1–3 more.',
    userCreateOakdaleCore4Desc: 'Always include the 5 Oakdale reflection questions, plus a couple more.',
    narratorVoice: 'Narrator Voice',
    narratorVoiceDesc: 'Voice used when generating audio for this plan\'s units.',
    voiceFemale: 'Female',
    voiceMale: 'Male',
    voiceBoth: 'Both',
    publishedLabel: 'Published', publishedDesc: 'Visible to enrolled users',
    unpublishedDesc: 'Hidden — only visible to you',
    // Journey mode
    journeyMode:'How would you like to grow?',
    growQuietly:'Grow Quietly',
    growQuietlyDesc:'A quiet space between you and God.',
    growWithAFew:'Grow with a Few',
    growWithAFewDesc:'Invite a few people to walk alongside you.',
    growWithCommunity:'Grow with Community',
    growWithCommunityDesc:'Journey together with your community.',
    growQuietlyBadge:'Personal Journey',
    growWithAFewBadge:(count)=>`Circle (${count})`,
    growWithCommunityBadge:(name)=>`With ${name}`,
    communityJourney:'Community Journey',
    circleJourney:'Growing Together',
    saveReflection:'Save Reflection',
    shareWithGroup:(name)=>`Share with ${name}`,
    shareWithCircle:'Share with Circle',
    inviteToCircle:'Invite to Circle',
    inviteMore:'Invite More',
    inviteOthers:'Invite others',
    convertSoloNote:'Inviting friends turns this private plan into a small circle. Your past reflections stay private.',
    circleInviteSent:'Invites',
    circleInvitePending:'Pending',
    circleInviteAccepted:'Joined',
    circleInviteDeclined:'Declined',
    circleInviteBanner:(name)=>`${name} invited you to grow together on this plan`,
    acceptInvite:'Accept',
    declineInvite:'Decline',
    searchConnections:'Search connections...',
    noConnections:'No connections yet. Connect with others first.',
    selectFriends:'Select friends to invite (optional)',
    selectCommunity:'Select a community...',
    noCommunities:'You are not a member of any communities yet.',
    // Community Growth tab
    communityGrowthTitle:'Growth Plans',
    communityGrowthDesc:'Growth plans members are doing together in this community.',
    startPlanWithCommunity:'Start a Growth Plan with this community',
    noGrowthPlans:'No growth plans yet',
    noGrowthPlansDesc:'Be the first to start a growth plan with this community.',
    cancel:'Cancel',
    done:'Done',
    invite:'Invite',
    loadingPassage:'Loading passage…',
    unlocksIn:'Unlocks in',
    categories: {
      faith_foundations:    'Faith Foundations',
      universal_human:      "I'm Just Human",
      battles_against_sin:  'Battles Against Sin',
      grief_and_loss:       'Grief & Loss',
      real_world:           'Real-World Situations',
      discipleship:         'Discipleship',
      church_community:     'Church & Community',
    },
    ratings: {
      everyone:            '✅ Everyone',
      everyoneDesc:        'Suitable for all ages',
      guidance_suggested:  '👁 Parental Guidance',
      guidanceDesc:        'Some mature themes — appropriate for teens and adults',
      mature:              '🔞 Mature',
      matureDesc:          'Addresses explicit sin, addiction, sexual integrity, or deep grief — adults only',
    },
    completeAndShareFirst:'Complete a day and tap "Share with Community" to be the first.',
    readMultipleTranslations:'Read in Multiple Translations',
    // Discovery layout
    popularRow:'Popular',
    newRow:'New',
    seeAll:'See all',
    backToAll:'All categories',
    featuredLabel:'Featured',
    ratingBadge:{
      everyone:'E',
      guidance_suggested:'PG',
      mature:'M',
    },
    participantsShort:(n)=>`${n} joined`,
    forEveryone:'For Everyone',
    matureTopics:'Mature Topics',
    matureHiddenNotice:'Some plans with mature content are hidden. Update your date of birth in your profile to unlock them.',
    ageGate:{
      title:'Adults Only (18+)',
      body:'This plan covers mature topics intended for adults 18 and older. Update your date of birth in your profile settings to unlock it.',
      matureLockedBtn:'Locked — Adults Only',
      goToProfile:'Update Profile',
      close:'Close',
    },
  },

  faithCheckin: {
    labels:      ['Crisis', 'Distant', 'Going through the motions', 'Trying', 'Present', 'Connected', 'Close'],
    labelsShort: ['Crisis', 'Distant', 'Motions', 'Trying', 'Present', 'Connected', 'Close'],
    modalTitle:  'Where are you with God today?',
    modalSub:    'Takes 5 seconds. Completely private.',
    notePlaceholder: 'Anything on your heart? (optional)',
    skip:        'Skip for today',
    save:        'Save ✓',
    done:        'Noted. See you tomorrow.',
    back:        '← Back',
    checkInBtn:  '+ Check in',
    chartTitle:  'Where I Am With God',
    chartSub:    'Your private relationship trajectory — last 90 days',
    emptyHint:   'Your first check-in will start your trajectory.',
    checkInNow:  'Check in now',
    lastCheckin: 'Last check-in',
    avg90:       '90-day avg',
    trend7:      '7-day trend',
    closer:      'Closer',
    movingAway:  'Moving away',
    steady:      'Steady',
    checkins:    'Check-ins',
    recentEntries: 'Recent entries',
    noCheckin:   'No check-in yet today — tap to check in',
    endLow:      'Crisis',
    endHigh:     'Close',
    streakLabel: (n) => `🔥 ${n}-day streak`,
    profileTitle: 'Where I Am With God',
    profileToday: (label) => `${label} · today — tap to see trajectory`,
  },

  evangelism: {
    modalTitle:   'Who did you share Jesus with this week?',
    modalSub:     'A quiet weekly rhythm. Completely private.',
    shareTypes: {
      conversation: 'Conversation',
      invite:       'Invite',
      gospel:       'Gospel',
      prayer:       'Prayer',
      serve:        'Served',
    },
    addPlaceholder:  'Add a name…',
    notePlaceholder: 'Anything to remember about this week? (optional)',
    skip:            'Nothing this week',
    save:            'Save ✓',
    doneTitle:       'Well done, faithful one.',
    doneEmptyTitle:  'Noted.',
    donePeople:      (n) => n === 1 ? 'You planted a seed in 1 life this week.' : `You planted seeds in ${n} lives this week.`,
    doneEmptySub:    'There is grace for every week. See you next Sunday.',
    doneClose:       'Amen',
    streakLabel:     (n) => `🔥 ${n}-week streak`,
    addToPrayer:     'Pray for them',
    addedToPrayer:   'On your list',
    shareTestimony:  'Share a testimony',
    testimonySeed:   (names) => `This week I had the joy of sharing Jesus with ${names}. `,
    cardTitle:       'Weekly evangelism check-in',
    cardPrompt:      'Who did you share Jesus with this week?',
    cardDone:        (n) => n === 1 ? 'Checked in · 1 person this week' : `Checked in · ${n} people this week`,
    cardSkipped:     'Checked in for this week',
    statsTitle:      'Sharing Jesus',
    emptyHint:       'Your first check-in starts the story. Tap to begin.',
    statPeople:      'People reached',
    statWeeks:       'Weeks active',
    statStreak:      'Week streak',
    recurringTitle:  'People you keep investing in',
    weeksCount:      (n) => n === 1 ? '1 week' : `${n} weeks`,
  },

  userProfile: {
    failedLoad:'Could not load profile', goBack:'Go Back',
    recentPosts:'Recent Posts',
    removeConfirm:(name)=>`Remove ${name} as a connection?`,
    connect:'Connect', pending:'Request Sent', acceptRequest:'Accept Request', connected:'Connected',
    stats:{
      posts:'Posts', groups:'Groups', prayersSaid:'Prayers Said',
      prayingFor:'Prayer Recipients', prayerRecipients:'Prayer Recipients',
      streak:'Current Streak', bestStreak:'Best Streak',
      bibleStudies:(n=>'Bible Studies'), growthCompleted:(n=>'Plans Done'),
      daysActive:(n=>'Days Active'),
    },
    postTypes:{
      scripture:'Scripture', reflection:'Reflection', prayer:'Prayer', praise:'Praise', surrender:'Surrender',
    },
    commentsTotal:(n)=>`${n} comments total`,
  },

  chat: {
    title:'Messages', subtitle:'Encourage one another daily',
    sharedStudyLabel:'Bible Study',
    sharedMovementLabel:'Movement Story',
    directMessages:'Direct Messages',
    communities:'Communities',
    noConversations:'No conversations yet',
    noConversationsDesc:'Join a community or connect with someone to start chatting.',
    somethingWrong:'Something went wrong',
    tryAgain:'Try Again',
    noMessages:'No messages yet',
    beFirst:'Be the first to say something!',
    startDm:(name)=>`Start a conversation with ${name}`,
    messagePlaceholder:'Message',
    failedSend:'Failed to send message',
    failedLoad:'Could not load messages. Please try again.',
    members:(n)=>`${n} members`,
    groupChat:'Group chat',
    newMessage:'New Message',
    searchConnections:'Search connections…',
    noResults:'No matches found',
    noConnections:'No connections yet. Connect with people to message them.',
    // Editing a sent message (5 minutes) and the admin-only history.
    edit:'Edit',
    edited:'Edited',
    editing:'Editing message',
    cancelEdit:'Cancel',
    deleteMessage:'Delete message',
    saveEdit:'Save',
    editWindowClosed:'Messages can only be edited for 5 minutes after sending.',
    editFailed:'Could not save your edit. Please try again.',
    editConflict:'This message was changed somewhere else. Please try again.',
    historyTitle:'Edit history',
    historyNote:'Visible to admins only. Opening this is recorded in the audit log.',
    historyOriginal:'Original',
    historyEdit:'Edit',
    historyCurrent:'Current',
    historyFailed:'Could not load the edit history.',
    viewHistory:'Edited — view history',
    // Day separators and the typing line in an open conversation.
    today:'Today',
    yesterday:'Yesterday',
    isTyping:(name)=>`${name} is typing…`,
    // Photos and videos in direct messages.
    attach:'Add photos or videos',
    attachTooMany:(n)=>`You can send up to ${n} photos or videos at a time.`,
    attachTooLarge:'That file is too large. Photos can be up to 10 MB and videos up to 100 MB.',
    attachUnsupported:'Only photos and videos (MP4, MOV, WebM) can be sent.',
    attachUnprocessable:"This photo couldn't be prepared on this device. Try saving it as a JPEG first.",
    attachUploadFailed:"A file didn't upload. Remove it and try again.",
    removeAttachment:'Remove',
    uploading:'Uploading',
    openPhoto:'Open photo',
    close:'Close',
    photo:'Photo',
    photos:(n)=>`${n} photos`,
    video:'Video',
    videos:(n)=>`${n} videos`,
    mediaItems:(n)=>`${n} photos and videos`,
  },

  admin: {
    title:'Admin Console', subtitle:'Shepherd the flock',
    accessRequired:'Administrator Access Required',
    noPermission:"You don't have permission to view this page.",
    goHome:'Go Home',
    tabs:{
      dashboard:'Dashboard', users:'Users & Roles', userActivity:'User Activity', communities:'Communities',
      prayerEvents:'Prayer Events', prayerRecipients:'Prayer Recipients',
      posts:'All Posts', metrics:'Metrics', logs:'App Logs', auditLogs:'Audit Logs',
      prayNows:'Pray Now Prayers', bibleStudies:'Bible Studies', feedback:'Feedback', reports:'Reports',
      prayerWall:'Prayer Requests', growth:'Grow Deeper', well:'The Well', notifLogs:'Notif Logs',
      systemPrayers:'System Prayers', socialPosts:'Social Posts', movementStories:'Movement Stories',
      integrations:'AI Integrations',
      emails:'Email Campaigns',
      emailLogs:'Email Logs',
      appAccess:'Android Early Access',
      welcomeMessages:'Welcome Messages',
      blog:'Blog',
      newsletter:'Newsletter',
      settings:'Ignite Settings',
    },
    stats:{
      totalMembers:'Total Members', totalPosts:'Total Posts',
      prayerRecipients:'Prayer Recipients', prayerEvents:'Prayer Events',
      connections:'Connections', groups:'Groups',
    },
    search:'Search', refresh:'Refresh', delete:'Delete',
    confirmDelete:'Are you sure?',
    noResults:'No results found.',
    loading:'Loading…',
    roles:{ admin:'Administrator', user:'Community Member' },
    status:{ active:'Active', suspended:'Suspended', banned:'Banned' },
    providers:{ cognito:'Password', Google:'Google', SignInWithApple:'Apple' },
    users:{
      title:'Users & Roles', searchPlaceholder:'Search by name, email, username…',
      deleteConfirm:'Delete account permanently?',
      deleteWarning:'This will permanently delete the account and all associated data. This action cannot be undone.',
      deleteBtn:'Delete Account Permanently', deleteSuccess:'User deleted.',
      suspend:'Suspend', unsuspend:'Unsuspend', ban:'Ban',
      joined:'Joined', lastActive:'Last active', noUsers:'No users found.',
    },
    prayerEvents:{
      title:'Prayer Calendar Events', noEvents:'No prayer events found.',
    },
    prayerRecipients:{
      title:'Prayer Recipients', searchPlaceholder:'Search by name or prayer request…',
      noRecipients:'No prayer recipients found.',
    },
    posts:{
      title:'All Posts', noPosts:'No posts found.',
    },
    logs:{
      title:'App Logs', noLogs:'No log entries yet. Logs accumulate while the server is running.',
    },
    scheduler:{
      title:'Prayer Scheduler', runUrgent:'Run Urgent', runHigh:'Run High', runNormal:'Run Normal',
    },
    emails:{
      newCampaign:'New Campaign',
      saveDraft:'Save Draft', saving:'Saving…',
      testSend:'Send Test', sending:'Sending…', sendCampaign:'Send Campaign',
      confirmSend:'Confirm Send',
      titleLabel:'Campaign Title', titlePlaceholder:'e.g. Monthly Newsletter – April 2026',
      subjectLabel:'Subject Line', subjectPlaceholder:'What recipients see in their inbox',
      bodyLabel:'HTML Body', bodyPlaceholder:'<p>Hello,</p>\n<p>Your message here…</p>',
      audienceLabel:'Audience', audienceAll:'All Active Users', audienceAdmins:'Admins Only',
      audienceMembers:'Members Only', audienceCustom:'Custom Email List',
      customEmailsLabel:'Email Addresses (comma-separated)',
      testSendLabel:'Send test to', testEmailPlaceholder:'you@example.com',
      draft:'Draft', sent:'Sent',
      noCampaigns:'No email campaigns yet.',
      noCampaignsDesc:'Create your first campaign to send an announcement to your users.',
      deleteDraft:'Delete Draft',
      sampleRecipients:'Sample recipients:',
    },
  },

  report: {
    title:(label)=>`Report ${label}`,
    intro:(label)=>`Why are you reporting this ${label}?`,
    post:'post', community:'community', prayer_wall:'prayer request', growth_plan:'growth plan', movement_story:'movement story', content:'content',
    reasons: [
      { value:'spam',           label:'📢 Spam or unsolicited content' },
      { value:'harassment',     label:'😡 Harassment or bullying' },
      { value:'hate_speech',    label:'🚫 Hate speech or discrimination' },
      { value:'misinformation', label:'❌ Misinformation or false claims' },
      { value:'inappropriate',  label:'⚠️  Inappropriate or offensive content' },
      { value:'off_topic',      label:'🔇 Off-topic or irrelevant' },
      { value:'other',          label:'💬 Other' },
    ],
    detailsLabel:'Additional details',
    detailsOptional:'optional',
    detailsPlaceholder:'Any additional context that might help our review…',
    note:'Reports are anonymous. Repeated false reports may result in account action.',
    submit:'Submit report', submitting:'Submitting…',
    successTitle:'Report submitted',
    successBody:(label)=>`Thanks for helping keep Ignite safe. Our team will review this ${label} and take appropriate action.`,
    done:'Done',
    errorRetry:'Try again',
    couldNotSubmit:'Could not submit report. Please try again.',
  },

  journal: {
    title: 'My Journal',
    subtitle: 'Trace God\'s hand in your story',
    privateNote: 'Private — only visible to you',
    searchPlaceholder: 'Search your journal…',
    noResults: 'No entries match your search.',
    empty: 'Your journal is empty',
    emptyDesc: 'Your prayers, reflections, and Bible studies will appear here automatically. You can also add your own entries.',
    loadMore: 'Load more',
    loading: 'Loading…',
    allCaughtUp: 'All caught up',
    // Filters
    filterAll: 'All', filterPrayer: 'Prayer', filterPrayNow: 'Pray Now',
    filterBibleStudy: 'Bible Study', filterReflections: 'Reflections',
    filterWell: 'The Well',
    filterCheckin: 'Faith Check-In', filterEntries: 'My Entries',
    filterEvangelism: 'Evangelism',
    filterRhythm: 'Daily Rhythm',
    filterConversations: 'Conversations',
    sharedWith: (n) => n === 1 ? '1 person' : `${n} people`,
    // Entry types
    types: {
      prayer_event:      'Prayer',
      memorial_prayer:   'Memorial Prayer',
      pray_now:          'Pray Now',
      bible_study:       'Bible Study',
      brp_reflection:    'Bible Reading Plan',
      growth_reflection: 'Growth Plan',
      faith_checkin:     'Faith Check-In',
      evangelism_checkin: 'Evangelism Check-In',
      journal_entry:     'Journal Entry',
      well_discussion:   'Well Discussion',
      well_note:         'Sermon Notes',
      daily_rhythm_reflection: 'Daily Rhythm',
      encounter:         'Conversation',
    },
    // The Well
    wellPosted: 'Discussion in The Well',
    wellReplied: 'Replied in The Well',
    wellAskedQuestion: 'Asked a question in The Well',
    wellMyNotes: 'My Sermon Notes',
    // Prayer event
    prayedFor: 'Prayed for',
    remembering: 'Remembering',
    myNotes: 'My Notes',
    prayerGenerated: 'Generated Prayer',
    // Pray Now
    moment: 'Moment', tone: 'Tone', forWho: 'For', withWho: 'With', need: 'Need',
    // Bible study
    passage: 'Passage',
    aboutGod: 'What does this tell us about God?',
    aboutHumans: 'What does this tell us about humans?',
    howRespond: 'How might God be inviting you to respond?',
    sharePrompt: 'Who came to mind to encourage?',
    // BRP
    day: 'Day', week: 'Week', plan: 'Plan', community: 'Community',
    reflectionQuestions: 'Reflection Questions',
    // Faith check-in
    checkinLevel: 'Where I was with God',
    checkinNote: 'Note',
    checkinLevels: ['Crisis','Distant','Going through the motions','Trying','Present','Connected','Close'],
    // Ad-hoc entry
    newEntry: 'New Entry',
    editEntry: 'Edit Entry',
    entryTitle: 'Title',
    entryTitlePlaceholder: 'Give this entry a title… (optional)',
    entryBody: "What's on your heart?",
    entryBodyPlaceholder: 'Write freely — this is just for you…',
    entryDate: 'Date',
    entryTags: 'Tags',
    entryTagsPlaceholder: 'e.g. gratitude, family, work',
    saveEntry: 'Save Entry',
    deleteEntry: 'Delete this entry?',
    savedEntry: 'Entry saved',
    // Favorites
    favOnly: 'Starred only',
    starred: 'Starred',
    unstar: 'Unstar',
    // Stats
    totalEntries: (n) => `${n} ${n === 1 ? 'entry' : 'entries'}`,
    totalWords: (n) => `${n.toLocaleString()} words`,
    totalPrayers: (n) => `${n} ${n === 1 ? 'prayer' : 'prayers'}`,
    streakDays: (n) => `${n}-day streak`,
    thisMonth: 'This month',
    thisWeek: 'This week',
    allTime: 'All time',
    // Week navigation
    prevWeek:  'Previous week',
    nextWeek:  'Next week',
    today:     'Today',
    jumpToDate:'Jump to date',
    weekTitle: ({ week, range }) => `Week ${week} · ${range}`,
    weekStats: ({ entries, days }) =>
      `${entries} ${entries === 1 ? 'entry' : 'entries'} · ${days} of 7 days`,
    emptyWeek:        'Nothing logged this week yet',
    emptyWeekDesc:    'As you pray, reflect, and check in, this week will fill itself in. You can also add a journal entry by hand.',
    emptyFavorites:   'No starred entries yet',
    searchScopedToWeek: 'Searching this week. Clear search to browse other weeks.',
    close: 'Close',
  },

  languages:{ en:'English', es:'Español' },

  prayNow: {
    title: 'Pray Now',
    subtitle: 'Pause. Breathe. Speak to God.',
    momentLabel: 'What kind of moment is this?',
    moments: {
      meal: 'Before a meal',
      meeting: 'Meeting / decision',
      family: 'With family',
      friends: 'With friends',
      need: 'Praying for someone in need',
      difficult: 'Difficult / emotional moment',
      celebration: 'Celebration / good news',
      general: 'Just need a general prayer',
      morning: 'Good morning',
      goodnight: 'Goodnight',
      sleepless: "Can't sleep",
      anxious: 'Anxious / worried',
    },
    needLabel: 'Anything specific to pray for?',
    needPlaceholder: 'e.g., wisdom, healing, peace, safe travel…',
    withLabel: 'Who is here with you?',
    withPlaceholder: 'e.g., my wife, parents, coworkers…',
    forLabel: "Is there someone we're lifting up?",
    forPlaceholder: 'e.g., a friend named Mark, our team…',
    toneLabel: 'Tone',
    tones: {
      comforting: 'Comforting',
      encouraging: 'Encouraging',
      faithful: 'Strong & faithful',
      calm: 'Calm & peaceful',
    },
    lengthLabel: 'Length',
    lengths: { short: 'Short', standard: 'Medium', long: 'Long', auto: 'Auto' },
    contextLabel: 'Any extra details?',
    contextPlaceholder: 'Optional: anything else you want included…',
    optional: 'optional',
    generate: 'Generate Prayer',
    generating: 'Generating prayer…',
    prayerLabel: 'Prayer',
    copy: 'Copy', copied: 'Copied!',
    newPrayer: 'New Prayer',
    share: 'Share',
    shareToFeed: 'Share to Feed',
    failedGenerate: 'Failed to generate prayer. Please try again.',
    loading: 'Composing your prayer…',
    loadingSub: 'This takes just a few seconds.',
    tabNew: 'New Prayer',
    tabHistory: 'History',
    historyEmpty: 'No saved prayers yet. Generate your first one above!',
    savedPrayers: (n) => n === 1 ? '1 saved prayer' : `${n} saved prayers`,
    viewPrayer: 'View prayer',
    deleteConfirm: 'Delete this prayer?',
    delete: 'Delete',
    cancel: 'Cancel',
    back: '← Back',
    momentLabel2: 'Moment',
    toneLabel2: 'Tone',
    withLabel2: 'With',
    forLabel2: 'For',
    needLabel2: 'Need',
    contextLabel2: 'Context',
  },
gettingStarted: {
    headerTitle: 'Getting Started',
    stepLabels: ['Prayer List', 'Prayer Requests', 'Your Profile', 'Install App'],

    // Step 1
    prayerTitle: 'Who do you want to pray for?',
    prayerDesc: 'Tap a relationship below and type in a name. Ignite will build your personal prayer calendar automatically.',
    privacyBadge: '100% Private',
    privacyDesc: "— only you can see who you're praying for. Your prayer list is never shared.",
    addAnother: (rel) => `Add another ${rel.toLowerCase()}`,
    namePlaceholder: (rel) => `${rel}'s name…`,
    skipForNow: 'Skip for now',
    savePeople: (n) => `Save ${n} ${n === 1 ? 'person' : 'people'}`,
    continue: 'Continue',
    couldNotSave: 'Could not save — please try again.',
    // Step 2 — Prayer Requests
    requestsTitle: 'Any prayer requests?',
    requestsDesc: 'Add things on your heart right now. You can always add more later.',
    requestsPrivacyDesc: '— your requests are private by default. You control who sees them.',
    requestPlaceholder: 'What are you believing God for?',
    addAnotherRequest: 'Add another request',
    saveRequests: (n) => `Save ${n} request${n===1?'':'s'}`,

    // Step 2
    accountTitle: 'Set up your profile',
    accountDesc: 'This helps others on Ignite know who you are. You can always update this later.',
    firstName: 'First Name', lastName: 'Last Name',
    firstNamePlaceholder: 'First name', lastNamePlaceholder: 'Last name',
    favoriteVerse: 'Favorite Bible Verse',
    favoriteVersePlaceholder: 'e.g. John 3:16 — For God so loved the world…',
    bio: 'Bio', bioOptional: '(optional)',
    bioPlaceholder: 'A little about yourself…',
    timezone: 'Timezone', prayerReminder: 'Daily Prayer Reminder',
    noReminder: 'No reminder',
    reminderNote: "We'll email you each morning with who to pray for at the time you set above.",

    // Step 3
    installTitle: 'Install Ignite on your device',
    installDesc: 'Add Ignite to your home screen for the full app experience — faster, offline-capable, and push notifications.',
    iphoneLabel: 'iPhone / iPad',
    androidLabel: 'Android',
    desktopNote: "You're on a desktop — bookmark this page and install on your phone for the best experience.",
    iosStep1: 'Open Ignite in Safari',
    iosStep1Note: "Must be Safari — Chrome on iOS doesn't support this yet",
    iosStep2: 'Tap the Share button ⎋ at the bottom of the screen',
    iosStep3: 'Scroll down and tap "Add to Home Screen"',
    iosStep4: 'Tap "Add" in the top right — done! 🎉',
    iosNote: 'Requires iOS 16.4+. After installing, push notifications will work automatically.',
    androidStep1: 'Open Ignite in Chrome',
    androidStep2: 'Tap the ⋮ menu in the top-right corner',
    androidStep3: 'Tap "Add to Home screen" or "Install app"',
    androidStep4: 'Tap "Install" — Ignite will appear on your home screen 🎉',
    emailInstructions: 'Want these instructions on your phone?',
    emailSentTo: (email) => `Sent to ${email}`,
    emailBtn: (email) => `Email to ${email}`,
    sending: 'Sending…',
    letsGo: "Let's go!",

    groups: {
      immediate: 'Immediate Family',
      extended: 'Extended Family',
      friends: 'Friends, Neighbors & Others',
    },

    // ── First Light ritual (Reverent tone) ──────────────────────────────────
    back: 'Back',
    // 1. Breathe
    breathTitle: ['Before we begin,', 'take a breath.'],
    breathHint: 'tap to begin',
    breathVerse: '“Cast all your anxiety on him, because he cares for you.”',
    breathVerseRef: '1 Peter 5:7',
    // 2. Name those you carry
    carryTitle: 'Name those you carry',
    carryDesc: 'Each name you speak lights a candle. They’ll never grow cold.',
    carryEmpty: 'The altar waits…',
    carryNamePh: (rel) => `Your ${String(rel).toLowerCase()}’s name…`,
    carryContinue: (n) => `Continue · ${n} carried`,
    addPerson: 'Add',
    release: (name) => `Release ${name}`,
    releaseShort: 'Release',
    you: 'You',
    carryConstHint: 'Tap a candle to see who it holds.',
    carryConstAria: (n) => `Your prayer constellation — ${n} ${n === 1 ? 'person' : 'people'} carried`,
    viewList: 'List view',
    viewConstellation: 'Constellation view',
    // 3. What season are you in?
    seasonTitle: 'What season are you in?',
    seasonDesc: 'Tell us what you’re walking through, and we’ll prepare a growth plan and a prayer just for you.',
    seasonOptional: 'Anything more you want to say… (optional)',
    seasonContinue: 'Continue',
    seasonSkip: 'Not now',
    season_college: 'In college / studying',
    season_spouse: 'Longing for a spouse',
    season_child: 'Waiting for a child',
    season_grief: 'Grieving a loss',
    season_habit: 'Fighting a habit',
    season_newfaith: 'New to faith',
    season_decision: 'Facing a big decision',
    season_anxiety: 'Anxious / overwhelmed',
    season_marriage: 'Working on my marriage',
    season_parenting: 'Parenting',
    season_work: 'Work & calling',
    season_health: 'Health / illness',
    season_other: 'Something else',
    blessPlanLine: 'A growth plan is being prepared for you.',
    planPreparing: 'Preparing your plan…',
    // legacy "Lay something down" copy (kept for safety)
    burdenTitle: 'Lay something down',
    burdenDesc: 'Is there a weight you’re carrying tonight? Leave it here.',
    burdenPh: 'Tonight I lay down…',
    burdenGo: 'Lay it down',
    burdenSkip: 'Not tonight',
    // 4. When will you come
    hourTitle: 'When will you come?',
    hourDesc: 'We’ll light the way and call you gently.',
    hour_dawn: 'Dawn · 6:00',
    hour_morning: 'Morning · 7:30',
    hour_midday: 'Midday · 12:00',
    hour_dusk: 'Dusk · 6:00',
    hour_night: 'Night · 9:00',
    // 5. And what is your name
    nameTitle: 'And what is your name?',
    nameDesc: 'So we may welcome you by it.',
    nameGo: 'Receive the blessing',
    // 6. Keep the flame close
    installTitle2: 'Keep the flame close',
    installDesc2: 'Add Ignite to your home screen so the light can always reach you.',
    installGo: 'The flame is kept',
    // Native-app promotion
    installAppDesc: 'Carry the light with you — keep Ignite close on your phone.',
    iosCardLabel: 'iPhone & iPad',
    androidCardLabel: 'Android',
    appStoreCta: 'Download on the App Store',
    playStoreCta: 'Open Google Play',
    androidEarlyAccess: 'Ignite for Android is in early access. Ask and we’ll open the door for you.',
    requestAccessCta: 'Request early access',
    requesting: 'Requesting…',
    requestSent: (email) => `We’ll open the door for ${email} soon.`,
    requestError: 'Could not send — please try again.',
    desktopAppNote: 'Continue here, or carry Ignite with you on your phone:',
    // 7. Go in peace
    blessTitle: (name) => name ? `Go in peace, ${name}.` : 'Go in peace.',
    blessDesc: (count, time) =>
      `${count ? `${count} ${count === 1 ? 'name is' : 'names are'} kept by candlelight.` : 'Your altar is kept.'}${time ? ` We’ll keep watch, and call you at ${time}.` : ' We’ll keep watch.'}`,
    blessGo: 'Enter Ignite',
  },
  prayerWall: {
    title: 'Prayer Requests',
    subtitle: 'Ask boldly. Watch God move.',
    pleaseHeader: 'Please',
    thankYouHeader: 'Thank You',
    pleaseDesc: 'Active prayer requests',
    thankYouDesc: 'Prayers God has answered',
    addRequest: 'Add Prayer Request',
    editRequest: 'Edit Request',
    newRequest: 'New Prayer Request',
    titleLabel: 'Title', titlePlaceholder: 'What are you praying for?',
    categoryLabel: 'Category',
    descriptionLabel: 'Notes / Description',
    descriptionPlaceholder: 'Share more details about this prayer...',
    endDateLabel: 'End Date',
    endDateHint: 'Optional — for time-sensitive prayers (surgery, trip, exam...)',
    visibilityLabel: 'Visibility',
    visPrivate: 'Private — only me',
    visConnections: 'My Connections',
    visGroups: 'My Communities',
    visAll: 'My Connections & Communities',
    // Options in the new/edit request form.
    formVis: {
      private: 'Private', privateDesc: 'Only visible to you',
      connections: 'My Connections', connectionsDesc: 'People connected to you',
      groups: 'My Communities', groupsDesc: 'Members of your communities',
      all: 'Connections & Communities', allDesc: 'Everyone you’re connected to, and everyone in your communities',
    },
    saveRequest: 'Save Prayer Request',
    markAnswered: 'God Answered This! 🙌',
    answeredTitle: 'How did God answer?',
    answeredPlaceholder: 'Share how God answered this prayer — your testimony may encourage others...',
    answeredLabel: 'How God answered',
    moveToThankYou: 'Move to Thank You',
    deleteConfirm: 'Remove this prayer request?',
    emptyPlease: 'No active prayer requests',
    emptyPleaseDesc: 'Add your first prayer request to get started.',
    emptyThankYou: 'No answered prayers yet',
    emptyThankYouDesc: 'When God answers your prayers, move them here to remember His faithfulness.',
    answeredOn: 'Answered',
    endsOn: 'Ends',
    sharedWith: 'Shared with',
    privateLabel: 'Private',
    // Translation badge
    translatedFrom: (lang) => `Translated from ${lang}`,
    showTranslation: 'Translate',
    showOriginal: 'Show original',
    langEnglish: 'English',
    langSpanish: 'Spanish',
    categories: ['Personal', 'Family', 'Health', 'Financial', 'Relationships', 'Church', 'Nation', 'World / Disaster', 'Discernment', 'Gratitude', 'Spiritual Growth', 'Work / Career', 'Other'],
    filterAll: 'All',
    filterMine: 'Mine',
    markAnsweredShort: 'Answered',
    filterPlease: 'Please',
    filterThankYou: 'Thank You',
    fromNetwork: 'From your network',
    prayedFor: 'I prayed for this',
    sharedByYou: 'Shared by you',
  },

  settings: {
    title: 'Settings',
    subtitle: 'Make Ignite yours',
    tabs: {
      login:'Login Info', notifications:'Notifications', connections:'Connections',
      invites:'Community Invites', faq:'Help & FAQ', feedback:'Feedback',
      privacy:'Privacy', gettingStarted:'Getting Started', account:'Account',
    },
    login: {
      emailSection:'Email Address', emailDesc:'Tap to change your sign-in email',
      emailCurrent:'Current email', newEmailLabel:'New email address',
      updateEmail:'Update email', emailUpdated:'Email updated. Check your inbox to verify.',
      newPasswordHint: 'At least 8 characters',
      emailError:'Could not update email',
      emailHiddenByApple:'Hidden by Apple',
      emailHiddenDesc:'You chose to keep your email private when signing in with Apple. Tap to set a sign-in email.',
      passwordSection:'Password', changePassword:'Change password',
      passwordDesc:'Update your Ignite sign-in password',
      currentPassword:'Current password', newPassword:'New password',
      confirmPassword:'Confirm new password', updatePassword:'Change password',
      passwordUpdated:'Password changed successfully.',
      passwordError:'Could not change password',
      passwordMismatch:'New passwords do not match',
      passwordTooShort:'Password must be at least 8 characters',
      linkedAccounts:'Linked Accounts',
      connected:'Connected', notConnected:'Not connected', notLinked:'Not linked',
      socialNote:'Social logins are managed through your Apple or Google account settings. To disconnect, remove Ignite from your linked apps there.',
    },
    invites: {
      noInvites:'No pending community invites',
      accept:'Accept', decline:'Decline', invitedBy:'Invited by',
      members:'members', joined:'Joined community!', declined:'Invite declined.',
      error:'Could not respond to invite',
    },
    account: {
      signOutSection:'Sign Out',
      signOut:'Sign out', signOutDesc:'Sign out of Ignite on this device',
      newsletterSection:'Newsletter',
      newsletterToggle:'Ignite newsletter',
      newsletterDescOn:"You'll receive the weekly devotional and occasional team notes.",
      newsletterDescOff:"You won't receive the Ignite newsletter. Tap to opt back in.",
      newsletterDescLoading:'Checking your subscription…',
      newsletterOn:'Subscribed',
      newsletterOff:'Unsubscribed',
      newsletterSubscribed:"You're subscribed.",
      newsletterUnsubscribed:"You've been unsubscribed.",
      dangerZone:'Danger Zone',
      deleteAccount:'Delete account',
      deleteWarning:'Permanently deletes your account and anonymises all your data. This cannot be undone.',
      deleteBtn:'Delete my account',
      typeToConfirm:'Type',
      confirmDelete:'Confirm delete',
      deleteError:'Could not delete account. Please contact support.',
      dataExportSection:'Your Data',
      downloadData:'Download my data',
      downloadDataDesc:'Export all your Ignite data as a JSON file',
      downloadDataSuccess:'Data export downloaded.',
      downloadDataError:'Could not export data. Please try again.',
    },
    feedback: {
      sectionTitle:'Submit Feedback',
      intro:"Found a bug? Have an idea? We read everything. Your feedback shapes what we build next.",
      category:'Category', selectCategory:'Select a category…',
      tellUsMore:'Tell us more', placeholder:'Describe the issue or idea in detail…',
      submit:'Submit feedback', submitting:'Submitting…',
      successMsg:"Thanks for your feedback! We'll review it soon.",
      errorMsg:'Could not submit feedback. Please try again.',
      cat_cosmetic:'Cosmetic / UI request',
      cat_feature:'New feature request',
      cat_bug_minor:'Minor bug',
      cat_bug_major:'Major functionality broken',
      cat_other:'Other / General feedback',
    },

    privacy: {
      sectionDiscovery: 'Profile & Discovery',
      sectionPosts: 'Posts',
      sectionPrayer: 'Prayer',
      sectionStats: 'Profile Statistics Visibility',
      sectionBlocked: 'Blocked Users',
      saved: 'Privacy settings saved.',
      saveFailed: 'Could not save. Please try again.',
      saving: 'Saving…',
      profileVisLabel: 'Profile Visibility',
      profileVisDesc: 'Who can view your profile',
      searchLabel: 'Appear in Search',
      searchDesc: 'Let others find you by name or username',
      connReqLabel: 'Connection Requests',
      connReqDesc: 'Who can send you a connection request',
      activityLabel: 'Activity Status',
      activityDesc: 'Show when you joined and days active',
      lineageLabel: 'Share Lineage With My Disciples',
      lineageDesc: 'When on, the people you disciple can see who has discipled you, going all the way up the chain. When off, the tree stops at you when viewed from below.',
      postVisLabel: 'Default Post Visibility',
      postVisDesc: 'Pre-selected audience when creating a post',
      prayerNote: "Your prayer recipients and prayer calendar are always private — only you can see who you're praying for. You can still choose who sees your prayer statistics below.",
      statsHint: 'Click a stat to toggle between Everyone and My Connections. Locked stats are always private.',
      alwaysPrivate: 'Always private',
      clickToChange: 'Click to change',
      noBlocked: "You haven't blocked anyone. Blocked users can't see your profile, message you, or appear in your feed.",
      unblock: 'Unblock',
      visOptions: {
        public: 'Everyone', publicDesc: 'All Ignite users',
        connections: 'My Connections', connectionsDesc: "Only people you're connected with",
        private: 'Only Me', privateDesc: 'Hidden from others',
        everyone: 'Everyone', none: 'No One',
      },
      statLabels: {
        prayersSaid: 'Prayers Said',
        prayerListCount: 'Prayer Recipients',
        prayerRequestCount: 'Prayer Requests',
        prayerCompletionRate: 'Prayer Rate',
        currentStreak: 'Current Streak',
        bestStreak: 'Best Streak',
        bibleStudies: 'Bible Studies',
        growthCompleted: 'Plans Completed',
        daysActive: 'Days Active',
        communitiesJoined: 'Communities',
        deviceCount: 'My Devices',
        connections: 'Connections',
      },
    },
    gettingStarted: {
      title:'Setup & Onboarding',
      wizardLabel:'Getting Started wizard',
      wizardDesc:'Revisit the setup flow — build your prayer list, complete your profile, and install the app',
      quickLinks:'Quick Links',
      faqLabel:'Help & FAQ',
      faqDesc:'Browse frequently asked questions',
    },
  },

  notifSettings: {
    title: 'Notification Settings',
    subtitle: 'Stay connected on your terms',
    masterTitle: 'Master controls',
    masterDesc: 'Toggle an entire channel on or off',
    saved: 'Preferences saved',
    loadError: 'Could not load preferences',
    footnote: "Some notification types don't support email — those toggles are shown as inactive. Email notifications require a valid Resend API key.",
    channels: {
      email: 'Email', emailDesc: 'Sent to your inbox',
      web: 'Web', webDesc: 'Browser / desktop notification',
      app: 'App', appDesc: 'Mobile push notification',
    },
    groups: {
      social: 'Social', activity_feed: 'Activity Feed', connections: 'Connections',
      account: 'Account', communities: 'Communities', chat: 'Chat',
      prayer_calendar: 'Prayer Calendar', prayer_wall: 'Prayer Requests',
      grow_deeper: 'Grow Deeper',
      faith_checkin: 'Faith Check-in',
      disciplemaking: 'Disciple-Making',
      discipleship: 'Discipleship',
      the_well: 'The Well',
      daily_rhythm: 'Daily Rhythm',
    },
    types: {
      mention: 'Mentions', comment: 'Comments', comment_reply: 'Replies',
      reaction: 'Reactions', connection_request: 'Connection Requests',
      connection_accepted: 'Connections Accepted', community_join_request: 'Join Requests',
      community_request_accepted: 'Join Accepted', community_request_rejected: 'Join Declined',
      community_invite: 'Community Invites', new_post: 'New Posts',
      community_event: 'Events', dm_message: 'Direct Messages',
      prayer_reminder: 'Prayer Reminders', prayer_wall_prayer: 'Prayer Request Prayers',
      account_password_changed: 'Account Activity',
      community_new_post: 'New Community Posts',
      community_event_added: 'New Events',
      community_event_updated: 'Event Updates',
      chat_dm: 'Direct Messages',
      chat_community: 'Community Chat',
      prayer_prayed_for_you: 'Someone Prayed For You',
      prayer_wall_connection_request: 'Prayer Requests (Connections)',
      prayer_wall_community_request: 'Prayer Requests (Communities)',
      prayer_wall_connection_answered: 'Answered Prayer (Connections)',
      prayer_wall_community_answered: 'Answered Prayer (Communities)',
      growth_plan_reminder: 'Growth Plan Reminders',
      growth_plan_ready: 'Your growth plan is ready',
      growth_challenge_starting: 'Challenge Starting Tomorrow',
      growth_challenge_day1: 'Challenge Begins Today',
      growth_circle_invite: 'Circle invite',
      growth_circle_unit_completed: 'Circle teammate completed a unit',
      invite_accepted: 'Invite accepted',
      discipleship_request: 'Discipleship invitations',
      discipleship_accepted: 'Discipleship accepted',
      discipleship_ended: 'Discipleship ended',
      coaching_checkin_sent: 'Check-in waiting for you',
      coaching_checkin_answered: 'Answers ready to review',
      coaching_checkin_response: 'Discipler responded to your check-in',
      coaching_checkin_reminder: 'Check-in reminder',
      bible_plan_published: 'New Bible Reading Plan',
      faith_checkin_reminder: 'Evening check-in reminder',
      evangelism_weekly_checkin: 'Weekly evangelism check-in',
      well_published: 'New weekly thread',
      well_video: 'Sermon video added',
      well_reply: 'Replies in The Well',
      well_discussion: 'New Discussions',
      daily_rhythm_morning: 'Morning rhythm',
      daily_rhythm_evening: 'Evening rhythm',
    },
  },

  faq: {
    title: 'Help & FAQ',
    subtitle: 'We\'re here to help',
    answers: 'answers',
    searchPlaceholder: 'Search questions…',
    noMatch: 'No matching questions found.',
    cats: {
      all: 'All', gettingStarted: 'Getting Started', feed: 'Feed',
      prayer: 'Prayer Calendar', prayerWall: 'Prayer Requests', prayNow: 'Pray Now',
      bibleStudy: 'Bible Study', communities: 'Communities', connections: 'Connections',
      profile: 'Profile', notifications: 'Notifications', courses: 'Grow Deeper', account: 'Account',
    },
  },

  lineage: {
    title: 'My Lineage',
    subtitle: 'Your spiritual family',
    loading: 'Gathering your lineage…',
    loadFailed: 'We couldn\'t load your lineage right now.',
    retry: 'Try again',
    // Hero — verb framing: name actions, not identity labels.
    heroBoth: (discipling, beingDiscipled) =>
      `You're discipling ${discipling} ${discipling === 1 ? 'person' : 'people'}. ${beingDiscipled} ${beingDiscipled === 1 ? 'person is' : 'people are'} discipling you.`,
    heroDiscipling: (n) =>
      `You're discipling ${n} ${n === 1 ? 'person' : 'people'}.`,
    heroBeingDiscipled: (n) =>
      `${n} ${n === 1 ? 'person is' : 'people are'} discipling you.`,
    heroLineageExtra: (total) =>
      `${total} ${total === 1 ? 'person is' : 'people are'} in your lineage.`,
    heroEmptyTitle: 'Every disciple-maker started as a disciple.',
    heroEmptyBody: 'Invite someone to grow with you, or ask someone to disciple you.',
    heroEmptyInviteCta: 'Invite someone to grow with you',
    heroEmptyMentorCta: 'Ask someone to disciple you',
    // Upstream
    upstreamHeading: 'Those who planted you',
    privacyBoundary: 'those before, kept private',
    notActive: 'No longer active',
    joined: (when) => `Joined ${when}`,
    joinedShort: (when) => `joined ${when}`,
    inLineJoined: (n, when) => `${n} in their line · joined ${when}`,
    discipling: (rel) => `discipling you${rel ? ` · ${rel}` : ''}`,
    paused: (rel) => `paused${rel ? ` · ${rel}` : ''}`,
    pausedNoTime: 'paused',
    youTag: 'you',
    primaryTag: 'Primary',
    // Downstream
    downstreamHeading: 'Those you carry',
    downstreamEmpty: 'You haven\'t started walking with anyone yet.',
    descendants: (n) => `${n} ${n === 1 ? 'descendant' : 'descendants'}`,
    expand: 'Expand',
    collapse: 'Collapse',
    nodeActions: 'More actions',
    // Closing summary
    lineageSummary: (souls, depth) =>
      `${souls} ${souls === 1 ? 'soul grows' : 'souls grow'} in your line, ${depth} ${depth === 1 ? 'generation' : 'generations'} deep.`,
    // Relative time (check-in recency)
    rel: { now: 'just now', m: (n) => `${n}m ago`, h: (n) => `${n}h ago`, d: (n) => `${n}d ago`, w: (n) => `${n}w ago` },
    // Footer
    inviteCta: 'Invite someone to grow with you',
    truncatedNotice: 'Your lineage is larger than this view can show. Showing the first part of the tree.',
  },

  discipleship: {
    // ── From ConnectionsScreen ─────────────────────────────────────────────
    actions: {
      menuLabel: 'Discipleship actions',
      offerToWalk: (name) => `Offer to walk with ${name}`,
      askToWalk: (name) => `Ask ${name} to walk with you`,
      pendingPill: 'Discipleship pending',
    },
    // ── Request modal (from Connections) ──────────────────────────────────
    request: {
      offerTitle: (name) => `Offer to walk with ${name}`,
      offerBody: (name) =>
        `You're offering to walk with ${name} as their discipler. This is a real commitment from you — we recommend a conversation with ${name} before sending this.`,
      askTitle: (name) => `Ask ${name} to walk with you`,
      askBody: (name) =>
        `You're about to ask ${name} to walk with you as your discipler. This is a real commitment — we recommend talking with ${name} directly before sending this. The app records the relationship, but the discipleship lives in real life.`,
      send: 'Send Request',
      cancel: 'Not yet',
      sent: 'Request sent.',
      sendFailed: 'We couldn\'t send that request.',
      alreadyPending: 'You already have a pending request with this person.',
      alreadyActive: 'You\'re already walking with this person.',
      notConnected: 'You need to be connected with this person first.',
    },
    // ── Pending screen ────────────────────────────────────────────────────
    pending: {
      title: 'Pending Requests',
      subtitle: 'Invitations awaiting your response',
      loading: 'Loading requests…',
      loadFailed: 'We couldn\'t load your requests.',
      heading: 'Awaiting your response',
      empty: 'No invitations are waiting on you right now.',
      sentHeading: 'Requests you\'ve sent',
      sentEmpty: 'You haven\'t sent any discipleship requests.',
      waitingForResponse: 'Waiting for response.',
      lineAskedAsDiscipler: (name) =>
        `${name} is asking you to walk with them as their discipler.`,
      lineAskedAsDisciple: (name) =>
        `${name} is offering to walk with you as a discipler.`,
      lineYouOfferedToDisciple: (name) =>
        `You offered to walk with ${name} as their discipler.`,
      lineYouAskedForDiscipler: (name) =>
        `You asked ${name} to walk with you.`,
      accept: 'Accept',
      decline: 'Decline',
      declined: 'Declined.',
      accepted: 'Accepted.',
      acceptFailed: 'We couldn\'t accept that request.',
      declineFailed: 'We couldn\'t decline that request.',
    },
    // ── Accept confirmation modals ────────────────────────────────────────
    acceptModal: {
      asDisciplerTitle: (name) => `Walk with ${name} as their discipler?`,
      asDisciplerBody: (name) =>
        `${name} is asking you to walk with them as their discipler. This is a real commitment — we recommend talking with ${name} directly about what this looks like before accepting. The app records the relationship, but the discipleship lives in real life.`,
      asDiscipleTitle: (name) => `Let ${name} walk with you?`,
      asDiscipleBody: (name) =>
        `${name} is offering to walk with you as a discipler. Before accepting, take a moment to talk with them about what this would look like.`,
      confirm: 'Accept',
      cancel: 'Not yet',
    },
    // ── End relationship modal ────────────────────────────────────────────
    endModal: {
      title: (name) => `End your relationship with ${name}?`,
      body: (name) =>
        `Ending this means ${name} will no longer appear in your lineage as someone you're walking with. ${name} will be notified that the relationship has ended, but won't see any reason.`,
      reasonLabel: 'Anything you want to note for yourself? (Private — only you will see this.)',
      reasonPlaceholder: 'Optional…',
      confirm: 'End relationship',
      cancel: 'Cancel',
      ended: 'Relationship ended.',
      endFailed: 'We couldn\'t end this relationship.',
    },
    // ── Make-primary modal ────────────────────────────────────────────────
    primaryModal: {
      title: (name) => `Make ${name} your primary discipler?`,
      body: 'Your primary discipler is shown in your lineage as the direct line above you. You can change this any time.',
      confirm: 'Make Primary',
      cancel: 'Cancel',
      changed: 'Primary discipler updated.',
      changeFailed: 'We couldn\'t update your primary discipler.',
    },
    // ── Node menu items ───────────────────────────────────────────────────
    nodeMenu: {
      openProfile: 'Open profile',
      makePrimary: 'Make this my primary discipler',
      end: 'End this relationship',
    },
    // ── Nav ───────────────────────────────────────────────────────────────
    nav: {
      pending: 'Pending Requests',
      pendingDesc: 'Discipleship invitations',
    },
    // ── Pending callout on LineageScreen ──────────────────────────────────
    callout: {
      headline: (n) =>
        n === 1
          ? '1 invitation is waiting on you'
          : `${n} invitations are waiting on you`,
      sub: 'View pending requests',
    },
  },

  coaching: {
    // ── Walking With [Name] detail screen ─────────────────────────────────
    back: 'Back to lineage',
    loading: 'Loading check-ins…',
    loadFailed: 'We couldn\'t load this right now.',
    retry: 'Try again',
    title: (name) => `Walking with ${name}`,
    roleMentor: (name) => `You're discipling ${name}.`,
    roleMentee: (name) => `${name} is discipling you.`,
    // ── The Thread header / sections ──────────────────────────────────────
    subMentor: 'You\'re discipling them',
    subMentee: 'They\'re discipling you',
    since: (when) => `since ${when}`,
    cadenceLabel: 'Check-in',
    needsResponseLabel: 'Needs your response',
    yourTurnLabel: 'Your turn',
    yourNote: 'Your note',
    theirNote: 'Their note',
    awaitingAnswer: 'Awaiting their answers',
    expandRow: 'Show this check-in',
    collapseRow: 'Hide this check-in',
    threadEmptyMentor: 'No check-ins yet. Send one to start the conversation.',
    threadEmptyMentee: 'No check-ins yet. Your discipler will send one when it\'s time.',
    // ── Status / labels ───────────────────────────────────────────────────
    sentOn: (when) => `Sent ${when}`,
    statusSent: 'Awaiting your answer',
    statusSentMentor: 'Waiting on their answer',
    statusAnswered: 'Answered',
    statusResponded: 'Responded',
    answersHeading: 'Answers',
    responseHeading: 'Discipler\'s response',
    prayerHeading: 'A prayer over you',
    noAnswer: '(No answer)',
    // ── Compose (coach) ───────────────────────────────────────────────────
    newCheckin: 'New check-in',
    composeTitle: 'Send a check-in',
    pickTemplate: 'Choose a starting point',
    questionsHeading: 'Questions',
    questionsHint: 'Add, edit, or remove anything before you send.',
    addQuestion: 'Add a question',
    questionLabelPlaceholder: 'Question',
    helpTextPlaceholder: 'Optional helper text',
    removeQuestion: 'Remove',
    send: 'Send check-in',
    sending: 'Sending…',
    sent: 'Check-in sent.',
    sendFailed: 'We couldn\'t send that check-in.',
    needOneQuestion: 'Add at least one question first.',
    cancel: 'Cancel',
    // ── AI question bank (coach) ──────────────────────────────────────────
    suggestBtn: 'Suggest questions (AI)',
    suggesting: 'Thinking…',
    suggestAdded: 'Added suggested questions you can edit.',
    suggestFallbackNote: 'General coaching questions (personalize as you like).',
    suggestFailed: 'We couldn\'t suggest questions right now.',
    // ── Answer (trainee) ──────────────────────────────────────────────────
    answerTitle: 'Answer this check-in',
    answerPlaceholder: 'Your answer…',
    submit: 'Submit answers',
    submitting: 'Submitting…',
    submitted: 'Answers submitted.',
    submitFailed: 'We couldn\'t submit your answers.',
    needOneAnswer: 'Please answer at least one question.',
    // ── Respond (coach) ───────────────────────────────────────────────────
    respondTitle: 'Respond & pray',
    noteLabel: 'Your response',
    notePlaceholder: 'Encourage, challenge, or coach…',
    prayerLabel: 'A prayer (optional)',
    prayerPlaceholder: 'Pray over them…',
    respondSend: 'Send response',
    respondSending: 'Sending…',
    responded: 'Response sent.',
    respondFailed: 'We couldn\'t send your response.',
    needNoteOrPrayer: 'Add a response or a prayer first.',
    // ── Inboxes on the Lineage screen ─────────────────────────────────────
    inboxAnswerHeadline: (n) =>
      n === 1 ? '1 check-in is waiting for you' : `${n} check-ins are waiting for you`,
    inboxAnswerSub: 'Answer your discipler\'s check-ins',
    inboxResponseHeadline: (n) =>
      n === 1 ? '1 answer is ready to review' : `${n} answers are ready to review`,
    inboxResponseSub: 'Respond and pray over their check-ins',
  },

  planPreview: {
    notFound: 'Plan not found',
    notFoundDesc: 'This plan may have been removed or is no longer available.',
    exploreIgnite: 'Explore Ignite',
    signIn: 'Sign In',
    signUpToStart: 'Sign Up to Start This Plan',
    alreadyHaveAccount: 'Already have an account?',
    whatYoullCover: "What You'll Cover",
    growthPlan: 'Growth Plan',
    days: 'days',
    weeks: 'weeks',
    enrolled: 'enrolled',
    untitled: 'Untitled',
  },

  rhythm: {
    title: 'Daily Rhythm',
    subtitle: 'A day-by-day walk with your community',

    morning: 'Morning',
    evening: 'Evening',
    theme: 'Theme',
    focus: 'Focus',
    scripture: 'Scripture Reading',
    // A day reads as a devotional: the Morning card (key verse + devotional),
    // then Read, then Pray (Ask / Listen / Respond), then the Evening.
    devotional: 'Devotional',
    read: 'Read',
    pray: 'Pray',
    ask: 'Ask',
    listen: 'Listen',
    respond: 'Respond',
    aboutWeek: 'About this week',
    story: 'Story',
    prayerPrompt: 'Prayer Prompt',
    watchVideo: 'Watch the video',
    videoUnavailable: 'This video could not be loaded. Try refreshing the page.',
    storyPending: "The video for this story hasn't been posted yet. Check back soon.",

    dayN: (n) => `Day ${n}`,
    today: 'Today',
    untitledDay: "Today's rhythm",
    notYetOpen: 'Not open yet',
    nothingHere: 'Nothing has been added to this section.',
    reflected: 'You reflected on this day',
    otherSeasons: 'Other seasons',
    timesLabel: (time) => `Mornings at ${time}`,

    opensAt: (time) => `Opens at ${time}`,
    eveningAt: (time) => `Evening at ${time}`,
    inTime: (dur) => `in ${dur}`,
    notScheduled: 'Not scheduled yet',

    yourReflection: 'Your reflection',
    reflectionPlaceholder: "What's stirring in you today?",
    // The journal question when the organizer left a day's blank — from the
    // week's own "Keep a Journal" guidance. Mirrored server-side.
    defaultJournalPrompt: 'What is Jesus bringing to your attention today?',
    // The week's "Keep a Journal" guidance as four short optional fields under
    // the day's question. Must match dailyRhythm.fields.* in the backend locales.
    keepAJournal: 'Keep a journal',
    optional: 'Optional',
    // The feed card a slot mints when it opens (components/PostShareCard.js).
    feedCard: {
      tonightsStory: (title) => `Tonight's story: ${title}`,
      startToday:    "Start today's rhythm",
      watchStory:    "Watch tonight's story",
      openEvening:   "Open tonight's rhythm",
      video:         'Video',
    },
    fields: {
      stoodOut:   'Verses that stood out',
      thoughts:   'Thoughts or questions that surfaced',
      hearing:    'What you sense He may be saying to you',
      responding: "How you're responding",
    },
    myWeek: 'My week',
    myWeekSub: 'Everything you wrote this week, in one place.',
    lookBack: 'Look back on your week',
    onlyYou: 'Only you can see this page.',
    nothingWrittenYet: "You haven't written anything yet. Your reflections will gather here as you go.",
    lookingBack: 'Looking back on your week',
    lookingBackNote: 'Drawn from what you wrote this week.',
    summarize: 'Summarize my week',
    summarizing: 'Reading back through your week…',
    refreshSummary: 'Refresh with my latest entries',
    summaryFailed: "Couldn't put that together just now. Please try again in a moment.",
    summarizeHint: 'A short look back at the themes, the words you sensed from Him, and the steps you named, drawn only from what you wrote.',
    summaryOutOfDate: "You've written more since this was put together.",
    save: 'Save',
    saved: 'Saved',
    saveAndShare: 'Save and Share with Community',
    updateShared: 'Update what you shared',
    sharedNote: 'Shared with your community',
    shareTitle: 'Share with your community',
    shareHint: 'Choose what becomes visible to the rest of your community. Anything you leave unchecked stays private.',
    confirmShare: 'Share',
    unshare: 'Stop sharing',
    cancel: 'Cancel',
    communityReflections: (n) => n === 1 ? '1 shared reflection' : `${n} shared reflections`,
    someone: 'Someone',

    dayNotFound: "This day isn't part of the rhythm.",
    dayUnavailable: 'This rhythm is not available right now.',
    backToRhythm: 'Back to the rhythm',

    card: {
      openNow: 'Open now',
      eveningAt: (time) => `Evening opens at ${time}`,
      nextOn: (date) => `Next on ${date}`,
      viewAll: 'View the whole rhythm',
    },

    empty: {
      member: 'Your community has not started a rhythm yet.',
      organizer: 'Create a program to start a daily rhythm for this community.',
    },

    settings: {
      enable: 'Enable Daily Rhythm',
      description: 'Publish a morning and evening rhythm for each day of a season, with a notification when each part goes live.',
      disableConfirm: 'Turn off Daily Rhythm? Members will immediately lose access to every program and day until you turn it back on. Their own reflections stay in their Journal.',
      timezone: 'Timezone',
      timezoneHint: 'Publish times are read in this zone. Everyone in the community is notified at the same moment.',
      morningTime: 'Default morning time',
      eveningTime: 'Default evening time',
      defaultsHint: 'These seed a new program. Changing them never retimes a program that is already running.',
      managePrograms: 'Manage programs',
    },

    manage: {
      title: 'Daily Rhythm programs',
      createProgram: 'New program',
      create: 'Create program',
      programTitle: 'Program name',
      titlePlaceholder: 'IGNITE Week',
      description: 'Description',
      startDate: 'First day',
      endDate: 'Last day',
      timezone: 'Timezone',
      timezoneHint: 'Publish times are read in this zone.',
      morningTime: 'Morning opens at',
      eveningTime: 'Evening opens at',
      datesTitle: 'Dates',
      datesHint: 'Shortening the range removes the days outside it. A day someone has written a reflection on is always kept.',
      datesShrinkConfirm: 'Remove the days outside the new range? Days with reflections are kept.',
      saveDates: 'Save dates',
      firstDayNumber: 'Number the first day',
      firstDayOne: 'Day 1',
      firstDayZero: 'Day 0',
      firstDayHint: 'Choose Day 0 when the first date is an introduction, so the next day is Day 1.',
      oneDayPerDate: 'One entry is created for every date in the range. Leave any day blank and it simply will not notify.',
      noPrograms: 'No programs yet.',
      programMissing: 'That program no longer exists.',
      backToPrograms: 'Back to programs',
      backToProgram: 'Back to the program',
      dayCount: (n) => n === 1 ? '1 day' : `${n} days`,
      untitled: 'Untitled day',
      editDay: 'Edit this day',
      saveDay: 'Save day',
      saving: 'Saving…',
      savedTick: '✓ Saved',
      saveFailed: 'Could not save',
      actionFailed: 'Something went wrong. Try again.',

      status: { draft: 'Draft', published: 'Published', archived: 'Archived' },
      draftBanner: 'This program is still a draft. Members cannot see it yet.',
      publish: 'Publish',
      publishHint: 'Publishing makes the program visible and starts the daily notifications. Days whose time has already passed will not send a late notification.',
      archive: 'Archive',
      archiveConfirm: 'Archive this program? Members will no longer see it, but nothing is deleted.',
      delete: 'Delete',
      deleteConfirm: 'Delete this program and all of its days? This cannot be undone.',
      releaseNow: 'Publish this section now',
      releaseConfirm: 'Publish this section now and notify everyone in the community?',
      alreadySent: 'Notified',
      scheduledFor: (time) => `Opens at ${time}`,
      sent: 'sent',
      unfilledDays: (n) => n === 1
        ? '1 day has no content yet. It will not notify anyone until you add something.'
        : `${n} days have no content yet. They will not notify anyone until you add something.`,
      partialDays: (n) => n === 1
        ? '1 day is only half filled in.'
        : `${n} days are only half filled in.`,

      opensWithMorning: 'Opens with the Morning.',
      keyVerse: 'Key verse',
      keyVersePlaceholder: '“Be still, and know that I am God.”',
      keyVerseRef: 'Key verse reference',
      readGuide: 'How to read it',
      journalQuestion: 'Journal question',
      storyTitlePlaceholder: 'Hearing God’s Voice',
      introHint: 'Shown at the top of the program page, collapsed. How the week works: fasting, journaling, the evening videos.',
      saveIntro: 'Save',
      journalPromptPlaceholder: 'What did you hear from Jesus today?',
      journalPromptHint: 'The question members answer in their reflection. Leave it blank to use the default: “What is Jesus bringing to your attention today?”',
      storyTitle: 'Story title',
      uploadVideo: 'Upload a video',
      orVideoLink: 'Or paste a video link',
      videoLinkHint: 'YouTube or Vimeo. If you also upload a file, the upload is used.',
      themePlaceholder: 'God, Speak to Me',
      focusPlaceholder: 'Hearing God’s Voice',
      overrideTime: 'Different time for this day',
      useProgramDefault: (time) => `Use the program default (${time})`,
      usingDefault: (time) => `Using the program default — ${time}`,
      previewAsMember: 'Preview as a member',
      previewBanner: 'This is what a member sees right now.',
      previewNote: 'You can see everything because you organize this community. Members only see a section once it opens.',
    },
  },

  well: {
    title:               'The Well',
    subtitle:            'Continue the conversation',
    noThread:            'No thread yet this week',
    newThread:           'New Thread',
    thisWeek:            'This Week',
    previousWeeks:       'Previous Weeks',
    sermonNotes:         'Sermon Notes',
    sermonInfo:          'Sermon Info',
    scripture:           'Scripture',
    myNotes: {
      title:               'My Notes',
      privacy:             'Only visible to you',
      addPrompt:           'Add a note…',
      addingTo:            'Adding personal note to',
      composerPlaceholder: 'Capture a thought, a question, a verse…',
      add:                 'Add',
      addHint:             '⌘/Ctrl+Enter to add',
      empty:               'Your notes will appear here as you add them.',
      edit:                'Edit',
      delete:              'Delete',
      deleteConfirm:       'Delete this note?',
      cancel:              'Cancel',
      save:                'Save',
      editError:           'Could not save your note',
      deleteError:         'Could not delete your note',
      tooLong:             'Note too long — try splitting it into smaller snippets',
      tooMany:             'You\'ve reached the limit of notes for this sermon',
      colorLabel:          'Note color',
      colorCustom:         'Custom color',
      colors: {
        indigo:  'Reflection',
        amber:   'Key insight',
        rose:    'Conviction',
        emerald: 'Action',
        sky:     'Question',
        violet:  'Prayer',
        stone:   'Misc',
      },
    },
    verseNotes: {
      // Rendered inside the Scripture heading, so it carries its own
      // parentheses — a caption, not a sentence.
      hint:         '(tap a verse to note it)',
      barLabel:     'Selected verses',
      addNote:      'Note this',
      clear:        'Clear',
      // Interpolated keys are FUNCTIONS in this engine, not '{n}' placeholders
      // — t() calls the value with its extra arguments. A template string here
      // would render the braces literally.
      alreadyNoted: (n) => `${n} ${n === 1 ? 'note' : 'notes'} here already`,
      passageGone:  'This passage is no longer on the thread.',
    },
    discussionQuestions: 'Questions to Consider',
    openInBible:         'Open Scripture',
    challenge:           "This Week's Challenge",
    challengeShare:      'Share Your Response',
    challengePlaceholder:'What did you do or experience this week?',
    challengeResponses:  'Community Responses',
    challengeSubmitted:  'Thanks for sharing — your response is in.',
    discussion:          'Discussion',
    sortLabel:           'Sort discussions',
    sortNew:             'New',
    sortTop:             'Top',
    reply:               'Reply',
    replyPlaceholder:    'Join the discussion…',
    replyTo:             (name) => `Reply to ${name}`,
    starter: {
      triggerLabel:    'Continue the conversation from this week’s message…',
      triggerCta:      'Start a discussion',
      modalEyebrow:    'Add to the discussion',
      placeholder:     'Share a thought, a question, or a verse the message stirred…',
      send:            'Post',
      cancel:          'Cancel',
      close:           'Close',
      postError:       'Could not post',
    },
    replyDeleted:        '[This post was removed]',
    continueThread:      'Continue thread →',
    yourReply:           'Your reply',
    send:                'Send',
    pin:                 'Pin Post',
    unpin:               'Unpin Post',
    edit:                'Edit Post',
    delete:              'Delete Post',
    deleteConfirm:       'Delete this post? This cannot be undone.',
    report:              'Report Post',
    reportConfirm:       'Report this post for review?',
    reportSent:          'Thanks — our team will review this post.',
    pinned:              'Pinned',
    questionBadge:       'Question',
    by:                  'by',
    moreOptions:         'More options',
    reactions: {
      amen:    'Amen',
      praying: 'Praying',
      fire:    'Fire',
      heart:   'Heart',
      viewAll: 'View all reactions',
    },
    video: {
      add:           'Add Sermon Video',
      watch:         'Watch Sermon',
      label:         'Sermon Video URL',
      placeholder:   'https://youtube.com/...',
      invalid:       'Please enter a valid YouTube or Vimeo URL',
      save:          'Save Video',
    },
    settings: {
      title:        'The Well',
      subtitle:     'Weekly sermon discussion for your community',
      enable:       'Enable The Well',
      sourceUrl:    'Sermon Source URL',
      sourceUrlPlaceholder: 'https://yourchurch.com/thisweek',
      sourceUrlHelper: "Paste your church's weekly sermon page. Automation is currently supported for Oakdale Church. For other churches, we'll add support as communities join — we'll reach out when yours is ready.",
      autoPost:     'Auto-post Sunday morning',
      autoPostHelper: "Sermon info will be scraped and posted automatically each Sunday at 8 AM ET. You'll receive a preview email around 7 AM ET to review first.",
      futureNote:   'Automation for this URL will be added in a future update.',
      saved:        'Settings saved',
      saveError:    'Could not save settings',
      fetchNow:     'Fetch & Generate Now',
      fetchNowHelper: 'Run the Sunday scraper now and create a draft thread you can review and publish.',
      fetchSuccess: 'Draft created — opening it now…',
      fetchError:   'Could not fetch sermon. Check the URL and try again.',
      viewDraft:    'View Draft',
      lastFetched:  'Last fetched',
      disableConfirm: 'Turn off The Well? Members will lose access to existing sermon threads until you re-enable it.',
      nonOakdaleNotice: 'Auto-fetch and Sunday auto-post currently only work for oakdale.church URLs. You can still create threads manually.',
    },
    admin: {
      manageThreads:  'Manage Threads',
      newThread:      'New Thread',
      editThread:     'Edit Thread',
      saveDraft:        'Save Draft',
      publishNow:       'Publish Now',
      scheduleSunday:   'Schedule for Sunday',
      save:             'Save',
      unpublishToDraft: 'Unpublish & Save as Draft',
      moveToDraft:    'Move to Draft',
      statusDraft:     'Draft',
      statusScheduled: 'Scheduled',
      statusPublished: 'Published',
      statusArchived:  'Archived',
      deleteThread:   'Delete',
      deleteConfirm:  'Delete this sermon thread? This will also delete all member posts, replies, reactions, and private notes. This cannot be undone.',
      scrapePreview:  'Scrape Preview',
      confirmScrape:  'Use This Data',
      regenerate:     'Regenerate AI Questions',
      seriesTitle:    'Series Title (optional)',
      sermonTitle:    'Sermon Title',
      speaker:        'Speaker (optional)',
      weekLabel:      'Week',
      scriptures:     'Scriptures',
      addScripture:   'Add Scripture',
      scriptureRef:   'Reference (e.g. John 3:16)',
      scriptureUrl:   'Link (bible.com)',
      notes:          'Sermon Notes',
      notesPlaceholder: 'Write the sermon points — use bold, italic, lists, etc.',
      scraperDiagnostics: 'Scraper source (diagnostics)',
      parsedNotes:    'Parsed notes',
      sourceHtml:     'Source HTML sample',
      photoUrl:       'Hero Photo',
      photoUpload:    'Upload hero photo',
      videoUrl:       'Sermon Video URL',
      challenge:      'Weekly Challenge',
      questions:      'Discussion Questions',
      addQuestion:    'Add Question',
      status:         'Status',
      statuses: {
        draft:     'Draft',
        scheduled: 'Scheduled',
        published: 'Published',
        archived:  'Archived',
      },
      preview:        'Preview',
      backToFeed:     'Back to The Well',
    },
    notification: {
      openTitle:      '☀️ The Well is open',
      videoTitle:     '🎥 Sermon video is now available',
      videoBody:      "Watch this week's message in The Well",
      replyTitle:     (username) => `${username} replied to you in The Well`,
    },
  },

  guide: {
    aboutPage: 'About This Page',
    gotIt: 'Got it',
    resetGuides: 'Reset Page Guides',
    resetGuidesDesc: 'Show the help buttons on each screen again',
    guidesReset: 'Page guides have been reset',
    // Each guide is keyed by the `screenKey` its screen passes to <PageGuide>.
    // Tabbed layouts ask for `<section>.<activeTab>`, so every tab needs an
    // entry or the sheet renders the raw key string. Keep es.js in step.
    //
    // These are prose, so no test sees them. When a feature's labels or
    // behaviour change, grep this block too — the copy below quotes UI labels.
    people: {
      people: {
        title: '👥 People',
        overview: 'Everyone you’re praying for and walking with, in one place. <b>This list is completely private</b> — nobody else can see who is on it or anything you’ve written.',
        features: [
          {
            icon: '➕', title: 'Add Anyone',
            desc: 'Tap <b>Add a person</b> for family, friends, coworkers — anyone on your heart. Add their relationship, what you’re praying for, and where they are on the journey. Use <b>Quick Add Recipients</b> on the Prayer calendar’s + menu to add several at once.',
          },
          {
            icon: '🕒', title: 'Prayer Cadence', accent: 'orange',
            desc: 'How often someone comes up on your prayer calendar:<br><b>Daily</b> — every day<br><b>Weekly</b> — once a week<br><b>Regular Rotation</b> — one person a day, working through your list<br>It is a rhythm, not a ranking.',
          },
          {
            icon: '🌱', title: 'Where They Are', accent: 'green',
            desc: 'Six stages, from <b>Till</b> (hard ground) through Soil, Seed, Sprout and Plant to <b>Harvest</b> (they’re discipling others). Only you move someone — a conversation debrief can suggest a stage, but nothing changes until you choose it.',
          },
          {
            icon: '👤', title: 'Their Page', accent: 'purple',
            desc: 'Tap a name to open <b>About</b> (what you’re praying for, your prayers for them, details), <b>Walking With</b> (where they are and <b>What you know about them</b>), and <b>History</b> (every conversation and how things have changed). From there you can <b>Pray Today</b>, <b>Pray on Specific Day</b>, or <b>Plan a conversation</b>.',
          },
          {
            icon: '🔍', title: 'Find Someone', accent: 'gold',
            desc: 'Filter by cadence with <b>Praying:</b>, or switch on <b>Not yet following Jesus</b> to see only those who don’t yet know Him. A search box appears once your list grows. The ⋮ menu on each row also lets you <b>Invite to Ignite</b> with a message that fits where they are.',
          },
          {
            icon: '🕯️', title: 'Those Who Have Passed', accent: 'purple',
            desc: 'Mark someone as passed away to keep them here for remembrance, shown in purple. Choose how often to remember them — <b>on anniversaries</b> (their date of passing, All Saints Day, Christmas and Easter), <b>once a month</b>, or <b>quarterly</b> — and record their faith legacy. Their journey doesn’t continue here; only your prayers do.',
          },
        ],
      },
      encounters: {
        title: '💬 Encounters',
        overview: 'Plan a real conversation with someone, be guided through it, and note afterwards where you saw God. Everything here is private to you.',
        features: [
          {
            icon: '🗓️', title: 'Plan Before You Go',
            desc: 'Tap <b>Plan a conversation</b> and choose who it’s with and where you’ll be — coffee, in the truck, over a meal, on a walk, at work, on the boat, or somewhere else. Pick <b>Find out where they are</b> for a first conversation, or <b>Go one step further</b>. Then <b>Build the guide</b> — it’s shaped by their stage and what you know about them, not a script.',
          },
          {
            icon: '🧭', title: 'Guided, Not Scripted', accent: 'orange',
            desc: 'One thought at a time, in large type: something you might say, a question or two, and what to do if they open up or if they don’t. Swipe between cards. It loads once, so nothing spins while they’re waiting on you, and it remembers your place if your phone locks. <b>Write something down</b> to capture a note mid-conversation.',
          },
          {
            icon: '📝', title: 'Afterwards', accent: 'purple',
            desc: 'Tap <b>We are done</b> for three questions: <em>What did you learn about them? Where did you see God? What is the one next step?</em> You’ll get suggestions — notes to keep, a next step, things to pray today. Nothing is saved until you tap <b>Keep what I ticked</b>, and a suggested stage is always yours to decide.',
          },
          {
            icon: '📚', title: 'Your Conversations', accent: 'green',
            desc: '<b>Pick up where you left off</b> lists conversations you’ve planned; <b>Earlier conversations</b> are the ones you’ve debriefed. They also appear on each person’s History.',
          },
        ],
      },
      lineage: {
        title: '🌳 Lineage',
        overview: 'The people who have discipled you, and the people you are discipling — your spiritual family tree.',
        features: [
          {
            icon: '🤝', title: 'Both Directions',
            desc: 'Offer to walk with someone, or ask someone to walk with you. You need to be <b>connected</b> first, and <b>both sides have to agree</b> — nobody is added without accepting. Invitations waiting on you appear under <b>Pending Requests</b>.',
          },
          {
            icon: '🌲', title: 'Your Tree', accent: 'green',
            desc: '<b>Those who planted you</b> sit above you and <b>Those you carry</b> below — generations deep. Choose who is your primary discipler, or end a relationship from its menu; the other person is told it ended, never why.',
          },
          {
            icon: '💬', title: 'Check-ins', accent: 'orange',
            desc: 'Open a relationship to send a check-in, answer one, and respond and pray over the answers. Lineage tells you when a check-in or an answer is waiting for you.',
          },
          {
            icon: '🔐', title: 'What Others See', accent: 'purple',
            desc: 'You control whether the people you disciple can see who discipled <em>you</em>. Change it any time in Settings → Privacy → <b>Share Lineage With My Disciples</b>.',
          },
        ],
      },
    },

    // ── Prayer: one guide per tab ──────────────────────────
    // There is no `recipients` entry: /prayer/recipients redirects to /people,
    // and what that guide said now lives in people.people above.
    prayer: {
      calendar: {
        title: '📅 Prayer Calendar',
        overview: 'Your prayer calendar fills itself every day, so you never have to wonder who to pray for. It is <b>private</b> — only you can see it.',
        features: [
          {
            icon: '✨', title: 'Filled For You',
            desc: 'Each day brings everyone set to <b>Daily</b>, the people set to <b>Weekly</b> spread across the week, and <b>one person</b> from your Regular Rotation. One prayer request is added each day too, taken in turn from your own requests and the ones you’re Praying For. People who have passed are remembered on the days you chose.',
          },
          {
            icon: '🎨', title: 'Reading the Calendar', accent: 'gold',
            desc: 'Switch between <b>Week</b> and <b>Month</b>, or jump back to <b>Today</b>. Colours tell you what each prayer is — a person by their cadence, <b>My Request</b> for your own requests, and <b>From…</b> for someone else’s. A check means you’ve already prayed.',
          },
          {
            icon: '🙏', title: 'Pray & Track', accent: 'orange',
            desc: 'Tap any prayer to keep <b>My Notes</b>, <b>Generate Sample Prayer</b> for words when you need them, see <b>A next step you could take</b> for where that person is, and tap <b>I Prayed!</b> The card at the top shows how many are left today and your prayer streak.',
          },
          {
            icon: '🎙️', title: 'Pray Now & Add', accent: 'green',
            desc: '<b>Pray Now</b> writes a prayer for the moment you’re in. The <b>+</b> button adds a prayer request, a person, or several people at once with <b>Quick Add Recipients</b>.',
          },
          {
            icon: '🔒', title: 'Private', accent: 'purple',
            desc: 'Who you’re praying for and the prayers you generate are <b>visible only to you</b>. One exception: notes on your <em>own</em> prayer request update that request, so whoever you shared it with will see them.',
          },
        ],
      },
      requests: {
        title: '📋 Prayer Requests',
        overview: 'Share what you need prayer for, and keep track of the requests you’ve committed to pray for.',
        features: [
          {
            icon: '🔒', title: 'You Choose Who Sees It',
            desc: 'Every request has its own audience:<br><b>Private</b> — only you<br><b>My Connections</b> — people connected to you<br><b>My Communities</b> — all your communities, or just the ones you pick<br><b>Connections & Communities</b> — everyone you’re connected to and everyone in your communities<br>My Connections and My Communities can also be chosen together.',
          },
          {
            icon: '📝', title: 'My Requests', accent: 'green',
            desc: 'Give it a title, a category and notes, and an optional <b>End Date</b> for something time-sensitive. Post <b>updates</b> as things change. When God answers, tap <b>God answered!</b> and share how — it moves from <b>Please</b> to <b>Thank You</b> as a record of His faithfulness.',
          },
          {
            icon: '🙏', title: 'Praying For', accent: 'orange',
            desc: 'Requests you said yes to in Discover live under <b>Praying For</b>, where you can follow the requester’s updates and see when God has answered.',
          },
          {
            icon: '☰', title: 'More on Each Request', accent: 'purple',
            desc: 'Use a request’s menu to <b>Pray today</b>, <b>Pray on Specific Day</b>, <b>Share to Feed</b>, edit or add an update. On someone else’s request you can also report it or remove it from your list.',
          },
        ],
      },
      discover: {
        title: '🧭 Discover Requests',
        overview: 'Prayer requests from your network, <b>one card at a time</b>, for you to take up in prayer.',
        features: [
          {
            icon: '👉', title: 'Swipe Right — I’ll Pray',
            desc: '<b>Swipe right</b> (or tap <em>“I’ll pray”</em>) to commit to praying for it. It moves to <b>Praying For</b> on the Requests tab and joins the requests that take turns on your prayer calendar.',
          },
          {
            icon: '👈', title: 'Swipe Left — Pass', accent: 'orange',
            desc: '<b>Swipe left</b> (or tap <em>“Pass”</em>) to set it aside. No judgment — pray for what’s on your heart. Changed your mind? When you reach the end, <b>Revisit passed requests</b> brings them back.',
          },
          {
            icon: '🔍', title: 'What You’ll See', accent: 'purple',
            desc: 'Requests shared with you by <b>your connections</b> and <b>communities you belong to</b>, plus community prayers from Ignite. Requests from people you’ve blocked never appear. The tab shows a count when new ones arrive.',
          },
        ],
      },
    },

    // ── Pray Now ────────────────────────────────────────────
    prayNow: {
      title: '🙏 Pray Now',
      overview: 'A <b>personal prayer</b> for the moment you’re in — written for what’s happening, who you’re with, and how you want it to feel.',
      features: [
        {
          icon: '✨', title: 'Choose Your Moment',
          desc: 'Start with the moment: <b>before a meal</b>, <b>good morning</b>, <b>goodnight</b>, <b>can’t sleep</b>, <b>anxious / worried</b>, <b>a difficult moment</b>, <b>celebration</b>, a meeting or decision, with family or friends, and more.',
        },
        {
          icon: '🎵', title: 'Make It Yours', accent: 'orange',
          desc: 'Add anything specific, who is with you, and who you’re lifting up — all optional. Then pick a tone (<b>Comforting</b>, <b>Encouraging</b>, <b>Strong & faithful</b>, or <b>Calm & peaceful</b>) and a length.',
        },
        {
          icon: '📤', title: 'Pray It, Share It', accent: 'purple',
          desc: 'Read it aloud, copy it, or <b>Share to Feed</b>. Tap <b>New Prayer</b> to start again.',
        },
        {
          icon: '📚', title: 'Your History', accent: 'green',
          desc: 'Every prayer is saved under <b>History</b> to revisit any time, and appears in your Journal.',
        },
      ],
    },

    // ── Communities (the list) ──────────────────────────────
    communities: {
      title: '🏘️ Communities',
      overview: 'Find your people — churches, small groups, Bible studies, neighborhoods — and <b>grow together</b>.',
      features: [
        {
          icon: '🧭', title: 'Mine & Discover',
          desc: '<b>Mine</b> holds the communities you belong to and <b>Pending</b> the ones you’ve asked to join. <b>Discover</b> lets you search and filter by type; <b>Featured</b> communities are shown first.',
        },
        {
          icon: '🔐', title: 'Joining', accent: 'purple',
          desc: '<b>Public</b> communities you can join straight away. <b>Private</b> ones you can see, but you <b>Request to Join</b> and an organizer approves. <b>Hidden</b> ones are invite-only and never appear in Discover.',
        },
        {
          icon: '🧩', title: 'What’s Inside', accent: 'green',
          desc: 'Every community has a feed, members and a group chat. Depending on what its organizers switch on, it may also have a <b>calendar</b> (with a Zoom room), <b>Bible reading plans</b>, <b>growth plans</b>, <b>The Well</b> for sermon discussion, and a <b>Daily Rhythm</b> devotional.',
        },
        {
          icon: '🗝️', title: 'Start One', accent: 'gold',
          desc: 'Create a community and choose its type, who can join, an optional parent community, and which features it will use. You become its <b>organizer</b> and can add co-organizers.',
        },
      ],
    },

    // ── A community's Home / Feed ───────────────────────────
    communityHome: {
      title: '🏡 Your Community',
      overview: 'Everything this community shares, in two tabs: <b>Home</b> for what’s happening, and <b>Feed</b> for posts.',
      features: [
        {
          icon: '🏠', title: 'Home',
          desc: '<b>Happening now</b> appears when an event is live or about to start. Below it are <b>This week</b>’s events and cards for whatever this community uses — Daily Rhythm, The Well, the reading plan, and growth plans. Tap a card to open it.',
        },
        {
          icon: '📣', title: 'Feed', accent: 'orange',
          desc: 'Share a reflection, scripture, prayer, praise or surrender with the community, and react, comment and encourage one another. The feed also shows when someone joins, when a new plan or Well thread is posted, and reflections members choose to share.',
        },
        {
          icon: '💬', title: 'Chat', accent: 'gold',
          desc: 'The <b>Chat</b> button opens the community’s group chat in Messages — for quick conversations and prayer needs between gatherings.',
        },
        {
          icon: '⋯', title: 'More', accent: 'purple',
          desc: 'The <b>⋯</b> button holds Members, Invites, Sub-Communities, Daily Rhythm and Leave. Organizers also find <b>Community Features</b> (switch features on or off — nothing is deleted) and <b>Manage</b> there.',
        },
        {
          icon: '🗝️', title: 'Organizers', accent: 'green',
          desc: 'Organizers and co-organizers add calendar events, build reading plans and Daily Rhythm programs, run The Well, invite members, and approve <b>join requests</b> on the Members screen.',
        },
      ],
    },

    // ── Daily Rhythm ────────────────────────────────────────
    rhythm: {
      title: '🌅 Daily Rhythm',
      overview: 'A day-by-day devotional your community walks through together, with a <b>Morning</b> and an <b>Evening</b> each day.',
      features: [
        {
          icon: '⏰', title: 'Opens On Schedule',
          desc: 'Each day’s morning and evening open at set times, and you’ll get a notification when they do. Until then you’ll see when it opens. Past days stay open — missing one is ordinary, so go back whenever you like.',
        },
        {
          icon: '📖', title: 'Each Day', accent: 'orange',
          desc: 'The <b>Morning</b> brings a key verse and devotional; <b>Read</b> has the day’s Scripture (switch NIV / NLT / MSG) with guidance; <b>Pray</b> walks you through asking, listening and responding; the <b>Evening</b> brings a story and a prayer prompt.',
        },
        {
          icon: '✍️', title: 'Your Reflection', accent: 'green',
          desc: 'Write in <b>Your reflection</b>, and use <b>Keep a journal</b> for a few optional guided questions. <b>Save</b> keeps it private; <b>Save and Share with Community</b> lets you choose which answers others can read. Your reflections also appear in your Journal.',
        },
        {
          icon: '🔁', title: 'My Week', accent: 'purple',
          desc: '<b>My week</b> gathers everything you wrote in one place. Tap <b>Summarize my week</b> for a look back drawn only from your own words. Only you can see this page.',
        },
      ],
    },

    // ── Growth Plans ────────────────────────────────────────
    grow: {
      title: '🌿 Grow Deeper',
      overview: 'Plans to help you <b>grow deeper in your faith</b> — Scripture, pastoral commentary and reflection questions, at your own pace. <b>Bible Study</b> is the third tab here.',
      features: [
        {
          icon: '🧭', title: 'Explore Plans',
          desc: 'Browse <b>Popular</b>, <b>New</b> and plans by category — Faith Foundations, Discipleship, Battles Against Sin, Grief & Loss and more. Search, or filter by category, length, maturity, language, or <b>Active Challenge</b>.',
        },
        {
          icon: '🚀', title: 'Start a Plan', accent: 'orange',
          desc: 'Tap <em>“Start this plan”</em> and choose how to grow: <b>Grow Quietly</b> (just you and God), <b>Grow with a Few</b> (invite people to walk alongside you), or <b>Grow with Community</b>. You can tap <b>Invite others</b> later to turn a quiet plan into a small circle.',
        },
        {
          icon: '✨', title: 'Build Your Own', accent: 'gold',
          desc: 'Can’t find what you need? Tap <b>New plan</b> and describe what you’re walking through — Ignite will build a plan for you.',
        },
        {
          icon: '💬', title: 'Reflect & Share', accent: 'purple',
          desc: 'Answer each unit’s questions and save — privately, or shared with your circle or community in the plan’s <b>Journey Feed</b>. Each save brings a <b>personal blessing</b>, and every reflection appears in your Journal.',
        },
        {
          icon: '🎧', title: 'Listen Along', accent: 'teal',
          desc: 'When a unit has narration, tap play to hear the commentary — choose a woman’s or a man’s voice where both are offered, change the speed, and skip back or ahead.',
        },
        {
          icon: '✅', title: 'Progress & Challenges', accent: 'green',
          desc: '<b>My Plans</b> opens with <b>Continue where you left off</b>. <b>Challenges</b> unlock one unit a day so you move through together. Finish a plan to receive a <em>personal faith letter</em>.',
        },
      ],
    },

    // ── Feed ────────────────────────────────────────────────
    feed: {
      title: '🔥 Your Feed',
      overview: 'See what God is doing in the people around you — reflections, scripture, prayers, praise and more.',
      features: [
        {
          icon: '✍️', title: 'Share Something',
          desc: 'Choose a type: <b>Reflection</b>, <b>Scripture</b> (look up a verse to attach), <b>Prayer</b>, <b>Praise</b>, or <b>Surrender</b>. Add <b>up to ten photos</b> or a video. Prayer requests you share from the Prayer tab appear here too.',
        },
        {
          icon: '🌐', title: 'Who Sees It', accent: 'purple',
          desc: 'Pick an audience for each post: <b>Public</b>, <b>Connections & Groups</b>, <b>Connections Only</b>, or <b>My Groups Only</b>.',
        },
        {
          icon: '❤️', title: 'Respond', accent: 'orange',
          desc: 'React with 🙏 ❤️ 🔥 or 🙌 — your own reaction is highlighted, and tapping it again removes it. Comment and reply, <b>Repost</b> with your own words, <em>@mention</em> people, and tap a <em>#hashtag</em> to see related posts.',
        },
        {
          icon: '🔖', title: 'Filter & Save', accent: 'gold',
          desc: 'Use the chips to show one type of post. Bookmark a post to keep it under <b>Saved</b>.',
        },
      ],
    },

    // ── Journal ─────────────────────────────────────────────
    journal: {
      title: '📓 My Journal',
      overview: 'A <b>private timeline</b> of your spiritual life, one week at a time. Only you can see it.',
      features: [
        {
          icon: '✨', title: 'It Fills Itself',
          desc: 'Prayers you’ve prayed, <b>Pray Now</b> prayers, <b>Bible studies</b>, reading-plan and growth-plan <b>reflections</b>, <b>The Well</b> discussions and sermon notes, <b>Daily Rhythm</b> reflections, <b>conversations</b> you’ve debriefed, faith check-ins and evangelism check-ins all appear here on their own.',
        },
        {
          icon: '✍️', title: 'Write Your Own', accent: 'orange',
          desc: 'Add an entry any time with an optional title, tags and date — <b>backdate</b> it if you’re catching up. Open an entry to edit it or <b>star</b> it.',
        },
        {
          icon: '📅', title: 'Move Through Time', accent: 'purple',
          desc: 'Step between weeks, <b>Jump to date</b>, or tap <b>Today</b>. Tap a day in the strip to go straight to it.',
        },
        {
          icon: '🔍', title: 'Search & Filter', accent: 'green',
          desc: 'Filter by type, turn on <b>Starred only</b> to see your favorites from any week, or search this week by keyword. The summary shows the week’s prayers, Pray Now prayers, entries and words written.',
        },
      ],
    },

    // ── The Well (list and thread share one guide) ──────────
    well: {
      title: '💧 The Well',
      overview: 'A weekly sermon thread for your community — go deeper together through the week.',
      features: [
        {
          icon: '☀️', title: 'Each Week',
          desc: 'A new thread for each sermon, under <b>This Week</b> and <b>Previous Weeks</b>. <b>Sermon Info</b> holds the series, Scripture (switch NIV / NLT / MSG), sermon notes and video, and a challenge when one is set.',
        },
        {
          icon: '🔥', title: 'Discuss Together', accent: 'orange',
          desc: 'On <b>Discussion</b>, answer the week’s questions, start your own conversation, and reply to others. Sort by <b>New</b> or <b>Top</b>, and react to the thread.',
        },
        {
          icon: '📝', title: 'My Notes', accent: 'purple',
          desc: 'Keep private sermon notes, colour-coded by what they are — a key insight, a conviction, an action, a prayer. Tap verses and <b>Note this</b> to attach a note to them; those verses are underlined in your note’s colour. Notes appear in your Journal.',
        },
      ],
    },

    // ── Bible Study ─────────────────────────────────────────
    'bible-study': {
      title: '📖 Bible Study',
      overview: 'Study any passage with guided questions and <b>AI insights</b> that help you see what God is saying.',
      features: [
        {
          icon: '📖', title: 'Pick a Passage',
          desc: 'Choose a book, chapters and verses, then <b>Dive into Bible Study</b> — or tap <b>Inspire Me</b> and a passage is chosen for you. Read it in NIV, NLT and The Message right in the app, with links to other translations.',
        },
        {
          icon: '✨', title: 'Questions & Insights', accent: 'orange',
          desc: 'Start with <b>Background & Context</b>, then work through what the passage shows about God, about people, and how He’s inviting you to respond. Think first, then tap to reveal Ignite’s insight. A last question asks who you could encourage with it. Tap <b>Save My Answers</b> to keep them.',
        },
        {
          icon: '🔗', title: 'Share a Study', accent: 'purple',
          desc: 'Create a link for <b>Anyone with the link</b> or <b>Connections only</b>, then <b>Send in a message</b> if you like — your answers are included only if you choose. You can also share a study to your feed.',
        },
        {
          icon: '📚', title: 'Past Studies', accent: 'green',
          desc: 'Every study is saved. Tap <b>Past Studies</b> at the top to revisit the passage, insights and your answers.',
        },
      ],
    },

    // ── Stories (Home → Stories) ────────────────────────────
    stories: {
      title: '🌍 Movement Stories',
      overview: 'See what God is doing across the globe — and tell what He’s done in your life.',
      features: [
        {
          icon: '🧭', title: 'Explore',
          desc: 'Featured stories, then stories of salvation, healing, provision, persecution and revival from every region. React with 🙏 ❤️ 🔥 or 🙌. Tap <b>Map</b> to spin the globe and find stories by country.',
        },
        {
          icon: '✍️', title: 'Tell Your Story', accent: 'orange',
          desc: 'Tap <b>Share a story</b> and choose a starting point — <b>How I met Jesus</b>, <b>Grew up in faith</b>, <b>Coming back</b>, <b>A season of waiting</b>, <b>An answered prayer</b>, or <b>Start from scratch</b>. Answer short, skippable prompts and get a first draft in your voice, then edit until it sounds like you.',
        },
        {
          icon: '🔒', title: 'You Choose Who Sees It', accent: 'purple',
          desc: 'Each story is <b>Only me</b>, <b>My connections</b>, or <b>Anyone with the link</b>. A shareable story gets a link anyone can open, and after a quick review it can be featured here — anonymously if you prefer.',
        },
        {
          icon: '📚', title: 'My Stories', accent: 'green',
          desc: 'Write as many as you like — God’s work in your life isn’t a single chapter. Find them all under <b>My Stories</b> and on your profile.',
        },
      ],
    },
  },
};

export default en;
